import { api, setCsrfToken } from "./client";
export { phoneApi } from "./phone";
export type {
  PhoneCapabilityResponse,
  PhoneExtension,
  PhoneExtensionListResponse,
  PhoneExtensionStatus,
  PhonePushStatus,
  PhoneSipStatus,
} from "./phone";
export { connectorApi } from "./connectors";
export { dmCampaignApi, dmEngagementApi, dmSuppressionApi } from "./dm";
export { advertisersApi, advertiserPortalApi } from "./advertisers";
export { ocrApi, ocrUploadApi } from "./ocr";
export { contactsApi } from "./contacts";
export type { Contact, ContactImportRow, ContactInput, ContactListResponse } from "./contacts";
import type {
  AuthMe,
  PaginatedResponse,
  FaxTransmission,
  FaxDetail,
  DashboardStats,
  ProductAlias,
  ImportCodeMap,
  ProductMapping,
  DictionarySuggestion,
  DictionarySuggestionDecisionStatus,
  DictionarySuggestionRejectReason,
  PlatformTenant,
  PlatformTenantDetail,
  PlatformIntakeRecord,
  DemoTenantSummary,
  TrialApplicationItem,
  TrialCaseItem,
  TrialCasePhase,
  TrialFaxNumberInventoryItem,
  TrialNotificationDeliveryItem,
  TrialDataRetentionItem,
  TrialConversionItem,
  TrialConversionPreparationPreview,
  TrialConversionPreflight,
  AllowedFaxRecipient,
  RuntimeControls,
  UploadOcrStatus,
  FaxSchedulerHealth,
  UsageSummary,
  BillingPlan,
  Invoice,
  InvoiceDetail,
  BillingSummaryItem,
  PointBalance,
  PointTransaction,
  AutoChargeResponse,
  AutoChargeSetupInput,
  AutoChargeSettingsInput,
  ApiKey,
  ApiKeyManagementPolicy,
  ApiKeyCreateResponse,
  ApiKeyScope,
  OnboardingStatus,
  OnboardingCompleteResponse,
  AiAttributionStats,
  CvFunnelReport,
  DmSuppressionsResponse,
  FaxProvider,
  FaxRecipient,
  FaxRecipientInput,
  FaxRecipientListResponse,
  FaxSendDraft,
  FaxSendDraftInput,
  FaxScheduledSend,
} from "@/types";
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";
export type PlatformAdminStepUpOperation =
  | "billing.issue_all"
  | "tenant.user.password_reset"
  | "tenant.user.entra_rebind"
  | "tenant.user.create"
  | "tenant.user.role_update"
  | "tenant.active_state.update"
  | "tenant.send_policy.update"
  | "tenant.send_demo.enable"
  | "tenant.entra_oidc_policy.update"
  | "tenant.delivery_endpoint.create"
  | "tenant.delivery_endpoint.revoke"
  | "tenant.smtp_intake_credential.create"
  | "tenant.smtp_intake_credential.revoke"
  | "tenant.email_intake_token.update"
  | "tenant.notification_channel.manage"
  | "tenant.connector_config.manage"
  | "tenant.connector_oauth.manage"
  | "tenant.sessions.revoke"
  | "tenant.session.revoke"
  | "global.sessions.revoke"
  | "tenant.api_key.create"
  | "tenant.api_key.revoke"
  | "platform_admin.webauthn.register"
  | "platform_admin.webauthn.revoke";

export const platformAdminStepUpApi = {
  verify: (
    operation: "platform_admin.webauthn.register",
    code: string,
  ) =>
    api.post<{ nonce: string; expiresAt: string }>("/auth/platform-admin/step-up", {
      operation,
      code,
    }),
  tenantUserPasswordResetTarget: (tenantId: string, userId: string) =>
    `tenant:${tenantId}:user:${userId}:password`,
  tenantUserRoleUpdateTarget: (tenantId: string, userId: string, role: string) =>
    `tenant:${tenantId}:user:${userId}:role:${role}`,
  tenantApiKeyRevokeTarget: (tenantId: string, keyId: string) =>
    `tenant:${tenantId}:api-key:${keyId}:revoke`,
  tenantSmtpIntakeCredentialRevokeTarget: (tenantId: string, credentialId: string) =>
    `tenant:${tenantId}:smtp-intake-credential:${credentialId}:revoke`,
  webAuthnRegisterTarget: (userId: string) =>
    `platform-admin:${userId}:webauthn:register`,
  webAuthnRevokeTarget: (userId: string, credentialId: string) =>
    `platform-admin:${userId}:webauthn:${credentialId}:revoke`,
};

export type PlatformAdminWebAuthnCredential = {
  id: string;
  name: string;
  deviceType: string;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
};

export type PlatformAdminWebAuthnAuthenticationRequest =
  | { operation: "billing.issue_all"; year: number; month: number }
  | { operation: "tenant.api_key.revoke"; tenantId: string; keyId: string }
  | { operation: "platform_admin.webauthn.register" }
  | { operation: "platform_admin.webauthn.revoke"; credentialId: string }
  | { operation: "tenant.user.password_reset"; tenantId: string; userId: string }
  | { operation: "tenant.user.entra_rebind"; tenantId: string; userId: string }
  | { operation: "tenant.user.create"; tenantId: string; requestBody: Record<string, unknown> }
  | { operation: "tenant.user.role_update"; tenantId: string; userId: string; role: string }
  | { operation: "tenant.api_key.create"; tenantId: string; requestBody: Record<string, unknown> }
  | { operation: "tenant.email_intake_token.update"; tenantId: string; requestBody: Record<string, unknown> }
  | { operation: "tenant.smtp_intake_credential.create"; tenantId: string; requestBody: Record<string, unknown> }
  | { operation: "tenant.smtp_intake_credential.revoke"; tenantId: string; credentialId: string }
  | { operation: "tenant.notification_channel.manage"; tenantId: string; requestBody: Record<string, unknown> }
  | { operation: "tenant.connector_config.manage"; tenantId: string; requestBody: Record<string, unknown> }
  | { operation: "tenant.connector_oauth.manage"; tenantId: string; requestBody: Record<string, unknown> }
  | { operation: "tenant.active_state.update"; tenantId: string; requestBody: Record<string, unknown> }
  | { operation: "tenant.send_policy.update"; tenantId: string; requestBody: Record<string, unknown> }
  | { operation: "tenant.send_demo.enable"; tenantId: string; requestBody: Record<string, unknown> }
  | { operation: "tenant.entra_oidc_policy.update"; tenantId: string; requestBody: Record<string, unknown> }
  | { operation: "tenant.delivery_endpoint.create"; tenantId: string; requestBody: Record<string, unknown> }
  | { operation: "tenant.delivery_endpoint.revoke"; tenantId: string; endpointId: string }
  | { operation: "tenant.sessions.revoke"; tenantId: string; requestBody: Record<string, unknown> }
  | { operation: "tenant.session.revoke"; tenantId: string; sessionIndexId: string; requestBody: Record<string, unknown> }
  | { operation: "global.sessions.revoke"; requestBody: Record<string, unknown> };

export const platformAdminWebAuthnApi = {
  list: () => api.get<{ credentials: PlatformAdminWebAuthnCredential[] }>(
    "/auth/platform-admin/webauthn/credentials",
  ),
  registrationOptions: (stepUpNonce: string) =>
    api.postWithHeaders<PublicKeyCredentialCreationOptionsJSON>(
      "/auth/platform-admin/webauthn/registration/options",
      {},
      { "X-Platform-Admin-Step-Up": stepUpNonce },
    ),
  verifyRegistration: (response: RegistrationResponseJSON) =>
    api.post<{ ok: true; credential: PlatformAdminWebAuthnCredential }>(
      "/auth/platform-admin/webauthn/registration/verify",
      response,
    ),
  authenticationOptions: (request: PlatformAdminWebAuthnAuthenticationRequest) =>
    api.post<PublicKeyCredentialRequestOptionsJSON>(
    "/auth/platform-admin/webauthn/authentication/options",
    request,
  ),
  verifyAuthentication: (response: AuthenticationResponseJSON) =>
    api.post<{ nonce: string; expiresAt: string }>(
      "/auth/platform-admin/webauthn/authentication/verify",
      response,
    ),
  revoke: (credentialId: string, stepUpNonce: string) =>
    api.deleteWithHeaders<{ ok: true }>(
      `/auth/platform-admin/webauthn/credentials/${encodeURIComponent(credentialId)}`,
      { "X-Platform-Admin-Step-Up": stepUpNonce },
  ),
};

// Auth
type AuthLoginSuccess = { ok: true; csrfToken?: string; user: AuthMe["user"]; tenant: AuthMe["tenant"] };
type AuthMfaRequired = { ok: false; mfaRequired: true; challengeToken: string };
type AuthPasswordChangeRequired = {
  ok: false;
  passwordChangeRequired: true;
  challengeToken: string;
  policy: { minLength: number; requiredCharacterClasses: number; maxAgeDays: number };
};
export type AuthLoginResult = AuthLoginSuccess | AuthMfaRequired | AuthPasswordChangeRequired;

export const authApi = {
  me: async () => {
    const data = await api.get<AuthMe & { csrfToken?: string }>("/auth/me");
    if (data.csrfToken) setCsrfToken(data.csrfToken);
    return data;
  },
  logout: async () => {
    const data = await api.post<{ ok: boolean }>("/auth/logout", {});
    setCsrfToken(null);
    return data;
  },
  googleLogin: async (credential: string) => {
    const data = await api.post<AuthLoginResult>("/auth/google", { credential });
    if (data.ok && data.csrfToken) setCsrfToken(data.csrfToken);
    return data;
  },
  getGoogleClientId: () =>
    api.get<{ clientId: string }>("/auth/google-client-id"),
  microsoftStatus: () =>
    api.get<{ configured: boolean }>("/auth/microsoft/status"),
  startMicrosoftLogin: (returnTo?: string) =>
    api.post<{ authorizationUrl: string }>("/auth/microsoft/start", { returnTo }),
  startEnterpriseMicrosoftLogin: (email: string, returnTo?: string) =>
    api.post<{ authorizationUrl: string }>("/auth/microsoft/enterprise/start", { email, returnTo }),
  getMicrosoftLinkStatus: () =>
    api.get<{ configured: boolean; linked: boolean }>("/auth/microsoft/link/status"),
  startMicrosoftLink: () =>
    api.post<{ authorizationUrl: string }>("/auth/microsoft/link/start", {}),
  pendingMicrosoftMfa: () =>
    api.get<{ challengeToken: string }>("/auth/microsoft/pending-mfa"),
  enterpriseSsoStatus: () =>
    api.get<{ configured: boolean }>("/auth/sso/status"),
  startEnterpriseSso: (email: string) =>
    api.post<{ authorizationUrl: string }>("/auth/sso/start", { email }),
  pendingEnterpriseSsoMfa: () =>
    api.get<{ challengeToken: string }>("/auth/sso/pending-mfa"),
  login: async (email: string, password: string) => {
    const data = await api.post<AuthLoginResult>("/auth/login", { email, password });
    if (data.ok && data.csrfToken) setCsrfToken(data.csrfToken);
    return data;
  },
  changeExpiredPassword: async (challengeToken: string, newPassword: string) => {
    const data = await api.post<AuthLoginSuccess | AuthMfaRequired>("/auth/password/change-expired", { challengeToken, newPassword });
    if (data.ok && data.csrfToken) setCsrfToken(data.csrfToken);
    return data;
  },
  requestPasswordReset: (email: string) =>
    api.post<{ ok: true; message: string }>("/auth/password/reset/request", { email }),
  completePasswordReset: (token: string, newPassword: string, mfaCode?: string) =>
    api.post<{ ok: true }>("/auth/password/reset/complete", { token, newPassword, mfaCode }),
  acceptInvitation: async (token: string, password: string) => {
    const data = await api.post<{
      ok: true;
      csrfToken?: string;
      user?: AuthMe["user"];
      tenant?: AuthMe["tenant"];
    }>("/auth/invitations/accept", { token, password });
    if (data.csrfToken) setCsrfToken(data.csrfToken);
    return data;
  },
  verifyMfa: async (challengeToken: string, code: string) => {
    const data = await api.post<AuthLoginSuccess>("/auth/mfa/challenge", { challengeToken, code });
    if (data.csrfToken) setCsrfToken(data.csrfToken);
    return data;
  },
  passkeyAuthenticationOptions: (challengeToken: string) =>
    api.post<PublicKeyCredentialRequestOptionsJSON>("/auth/mfa/webauthn/authentication/options", { challengeToken }),
  verifyPasskey: async (challengeToken: string, response: AuthenticationResponseJSON) => {
    const data = await api.post<AuthLoginSuccess>("/auth/mfa/webauthn/authentication/verify", { challengeToken, response });
    if (data.csrfToken) setCsrfToken(data.csrfToken);
    return data;
  },
  startPlatformAdminTotpRecovery: (challengeToken: string) =>
    api.post<{ secret: string; uri: string; qrDataUrl: string; expiresAt: string }>(
      "/auth/mfa/platform-admin/totp-recovery/start",
      { challengeToken },
    ),
  verifyPlatformAdminTotpRecovery: async (challengeToken: string, code: string) => {
    const data = await api.post<AuthLoginSuccess & { recoveryCodes: string[] }>(
      "/auth/mfa/platform-admin/totp-recovery/verify",
      { challengeToken, code },
    );
    if (data.csrfToken) setCsrfToken(data.csrfToken);
    return data;
  },
  mfaStatus: () => api.get<{
    enabled: boolean;
    totpEnabled: boolean;
    required: boolean;
    enabledAt: string | null;
    recoveryCodesRemaining: number;
    passkeys: Array<{
      id: string;
      name: string;
      deviceType: string;
      backedUp: boolean;
      createdAt: string;
      lastUsedAt: string | null;
    }>;
  }>("/auth/mfa/status"),
  passkeyRegistrationOptions: () =>
    api.post<PublicKeyCredentialCreationOptionsJSON>("/auth/mfa/webauthn/registration/options", {}),
  registerPasskey: (name: string, response: RegistrationResponseJSON) =>
    api.post<{ ok: true; recoveryCodes: string[] }>("/auth/mfa/webauthn/registration/verify", { name, response }),
  deletePasskey: (credentialId: string) =>
    api.delete<{ ok: true }>(`/auth/mfa/webauthn/credentials/${encodeURIComponent(credentialId)}`),
  setupMfa: () => api.post<{ secret: string; uri: string; qrDataUrl: string }>("/auth/mfa/setup", {}),
  enableMfa: (code: string) => api.post<{ ok: true; recoveryCodes: string[] }>("/auth/mfa/enable", { code }),
  disableMfa: (code: string) => api.post<{ ok: true }>("/auth/mfa/disable", { code }),
};

// FAX
export const faxApi = {
  sent: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return api.get<PaginatedResponse<FaxTransmission>>(`/fax/sent?${qs}`);
  },
  resend: (queueItemId: string, idempotencyKey: string) =>
    api.postWithHeaders<{
      ok: true;
      queueItemId: string;
      resendOfQueueItemId: string;
      idempotent: boolean;
      phone: string;
      pages: number | null;
      pointsRequired: number | null;
    }>(`/fax/sent/${encodeURIComponent(queueItemId)}/resend`, {}, { "Idempotency-Key": idempotencyKey }),
  received: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return api.get<PaginatedResponse<FaxTransmission>>(`/fax/received?${qs}`);
  },
  setReceivedReadState: (historyIds: string[], read: boolean) =>
    api.put<{ ok: true; updated: number }>("/fax/received/read-state", { historyIds, read }),
  downloadReceivedPdf: (pdfUrl: string) => {
    const url = new URL(pdfUrl, window.location.origin);
    url.searchParams.set("download", "1");
    const apiPath = `${url.pathname}${url.search}`.replace(/^\/api/, "");
    return api.getBlob(apiPath);
  },
  boxes: () => api.get<{
    data: Array<{ id: string; faxNumber: string; notes: string | null }>;
    authorizationEnabled: boolean;
  }>("/fax/boxes"),
  get: (id: number) => api.get<FaxDetail>(`/fax/${id}`),
};

// Dashboard
export const dashboardApi = {
  stats: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    return api.get<DashboardStats>(`/dashboard/stats?${params}`);
  },
};

export type OrderConfirmationRun = {
  id: string;
  ocrResultId: string;
  version: number;
  status: "review_required" | "stale" | "send_claimed" | "queued" | "closed_external";
  confirmationNo: string | null;
  receivedAt: string;
  senderNumber: string | null;
  erpOrderNumbers: string[];
  customerOrderNumber: string;
  faxRecipient: { number: string; source: string };
  priceSources: string[];
  directShipping: Array<{
    erpOrderNumber: string;
    companyName?: string;
    recipientName?: string;
    postalCode?: string;
    address?: string;
    phone?: string;
  }>;
  faxEnabled: boolean;
  artifact: null | {
    id: string;
    sha256: string;
    sizeBytes: number;
    templateId: string;
    templateVersion: string;
    createdAt: string;
  };
  queue: null | {
    id: string;
    status: string;
    ictfaxTxId: number | null;
    lastError: string | null;
    submittedAt: string | null;
    completedAt: string | null;
  };
  closedBy: string | null;
  closedAt: string | null;
  closeReason: string | null;
  createdAt: string;
  updatedAt: string;
};

export const orderConfirmationApi = {
  runs: (params: { status?: string; ocrResultId?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.ocrResultId) query.set("ocrResultId", params.ocrResultId);
    const suffix = query.size ? `?${query.toString()}` : "";
    return api.get<{ data: OrderConfirmationRun[]; previewEnabled: boolean }>(`/order-confirmation/runs${suffix}`);
  },
  run: (id: string) =>
    api.get<{ data: OrderConfirmationRun }>(`/order-confirmation/runs/${encodeURIComponent(id)}`),
  ensureArtifact: (id: string) =>
    api.post<{ ok: true; data: { id: string; sha256: string; sizeBytes: number } }>(
      `/order-confirmation/runs/${encodeURIComponent(id)}/artifact`,
      {},
    ),
  closeExternal: (id: string, reason: string) =>
    api.post<{ ok: true }>(`/order-confirmation/runs/${encodeURIComponent(id)}/close-external`, { reason }),
  sendFax: (id: string) =>
    api.post<{
      ok: true;
      data: { queue: OrderConfirmationRun["queue"]; existing: boolean };
    }>(`/order-confirmation/runs/${encodeURIComponent(id)}/send`, {}),
  artifactUrl: (id: string) =>
    `/api/order-confirmation/runs/${encodeURIComponent(id)}/artifact`,
  resolvePricing: (data: {
    source?: "endo" | "kikan";
    orderDate?: string;
    traderId?: number | null;
    traderCode?: string | null;
    senderFax?: string | null;
    senderTel?: string | null;
    items: Array<{
      itemId?: number | null;
      itemCode?: string | null;
      itemName?: string | null;
      quantity: number;
      unitPrice?: number | null;
      taxRate?: number | null;
    }>;
    charges?: Array<{
      code?: string | null;
      label: string;
      amount: number;
      taxRate?: number | null;
    }>;
    defaultTaxRate?: number | null;
    taxDisplayMode?: "none" | "total_only" | "by_rate";
    roundingMode?: "trunc" | "floor" | "round";
    useInputPriceFallback?: boolean;
  }) =>
    api.post<{ ok: boolean; data: {
      source: "endo" | "kikan";
      orderDate: string;
      taxDisplayMode: "none" | "total_only" | "by_rate";
      roundingMode: "trunc" | "floor" | "round";
      trader: { traderId: number | null; traderCode: string | null; catalogId: number | null };
      items: Array<{
        itemCode: string;
        itemName: string;
        quantity: number;
        unitPrice: number | null;
        amount: number;
        taxRate: number;
        taxAmount: number;
        priceSource: "catalog" | "master" | "input" | "missing";
        matchedItemId: number | null;
      }>;
      charges: Array<{
        code: string | null;
        label: string;
        amount: number;
        taxRate: number;
        taxAmount: number;
      }>;
      summary: {
        itemSubtotal: number;
        chargeSubtotal: number;
        subtotalExclTax: number;
        taxTotal: number;
        totalInclTax: number;
        taxByRate: Array<{ rate: number; taxable: number; tax: number }>;
      };
      warnings: string[];
    } }>("/order-confirmation/pricing/resolve", data),
  preview: (data: {
    confirmationNo: string;
    orderDate?: string | null;
    issuedAt?: string;
    companyName: string;
    companyNameKana?: string | null;
    companyAddress?: string | null;
    layoutMode?: "combined" | "by_warehouse";
    title?: string;
    note?: string | null;
    templateId?: string;
    config?: Record<string, unknown>;
    html?: string;
    charges?: Array<{
      code?: string | null;
      label: string;
      amount: number;
    }>;
    subtotalExclTax?: number | null;
    taxTotal?: number | null;
    totalAmount?: number | null;
    taxDisplayMode?: "none" | "total_only" | "by_rate";
    taxByRate?: Array<{ rate: number; taxable?: number; tax: number }>;
    items: Array<{
      itemCode?: string;
      itemName: string;
      quantity: number;
      unitPrice?: number | null;
      warehouseId?: number | null;
      warehouseName?: string | null;
      shippingDate?: string | null;
    }>;
  }) => api.postBlob("/order-confirmation/preview", data),
  templates: () =>
    api.get<{ data: Array<{
      id: string;
      name: string;
      originalFileName: string;
      templateType?: "coordinate" | "html" | "structured";
      mapping: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    }> }>("/order-confirmation/templates"),
  importTemplate: (file: File, opts?: { name?: string; mapping?: Record<string, unknown> }) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts?.name) fd.append("name", opts.name);
    if (opts?.mapping) fd.append("mapping", JSON.stringify(opts.mapping));
    return api.upload<{ ok: boolean; template: {
      id: string;
      name: string;
      originalFileName: string;
      templateType?: "coordinate" | "html" | "structured";
      mapping: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    } }>("/order-confirmation/templates/import", fd);
  },
  generateHtmlTemplate: (file: File, opts?: { name?: string; model?: string }) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts?.name) fd.append("name", opts.name);
    if (opts?.model) fd.append("model", opts.model);
    return api.upload<{ ok: boolean; template: {
      id: string;
      name: string;
      originalFileName: string;
      templateType: "html";
      mapping: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    }; htmlPreview: string }>("/order-confirmation/templates/generate-html", fd);
  },
  getTemplateHtml: (id: string) =>
    api.get<{ html: string }>(`/order-confirmation/templates/${id}/html`),
  updateTemplateHtml: (id: string, html: string) =>
    api.put<{ ok: boolean; template: {
      id: string;
      name: string;
      templateType: "html";
      createdAt: string;
      updatedAt: string;
    } }>(`/order-confirmation/templates/${id}/html`, { html }),
  generateStructuredTemplate: (file: File, opts?: { name?: string; model?: string }) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts?.name) fd.append("name", opts.name);
    if (opts?.model) fd.append("model", opts.model);
    return api.upload<{ ok: boolean; template: {
      id: string;
      name: string;
      originalFileName: string;
      templateType: "structured";
      mapping: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    }; config: Record<string, unknown> }>("/order-confirmation/templates/generate", fd);
  },
  getTemplateConfig: (id: string) =>
    api.get<{ config: Record<string, unknown> }>(`/order-confirmation/templates/${id}/config`),
  updateTemplateConfig: (id: string, config: Record<string, unknown>) =>
    api.put<{ ok: boolean; template: {
      id: string;
      name: string;
      templateType: "structured";
      createdAt: string;
      updatedAt: string;
    }; config: Record<string, unknown> }>(`/order-confirmation/templates/${id}/config`, { config }),
  updateTemplateMapping: (id: string, mapping: Record<string, unknown>) =>
    api.put<{ ok: boolean; template: {
      id: string;
      name: string;
      originalFileName: string;
      mapping: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    } }>(`/order-confirmation/templates/${id}/mapping`, { mapping }),
  removeTemplate: (id: string) =>
    api.delete<{ ok: boolean }>(`/order-confirmation/templates/${id}`),
  templatePdfUrl: (id: string) =>
    `/api/order-confirmation/templates/${encodeURIComponent(id)}/pdf`,
};

// Settings
export { settingsApi } from "./settings";
export type {
  OcrDocumentProfileRow,
  OcrDocumentProfileListResponse,
  OcrFormDefinitionInput,
  OcrFormDefinitionRow,
  OcrFormDefinitionListResponse,
} from "./settings";

// Send FAX
export type FaxCoverSheetInput = {
  recipientCompany?: string;
  recipientName?: string;
  subject?: string;
  message?: string;
  senderContact?: string;
};

export type FaxSendRequest = {
  phone: string;
  files: File[];
  accountId?: number;
  provider?: FaxProvider;
  message?: string;
  cover?: FaxCoverSheetInput;
  idempotencyKey?: string;
  billingPreviewToken?: string;
};

export type FaxSendResponse = {
  ok: boolean;
  provider: FaxProvider;
  phone: string;
  transmissionId?: number;
  documentId?: number;
  queueItemId?: string;
  queued?: boolean;
  gatewayMessageId?: string | null;
  pageCount?: number;
  pointsReserved?: number | null;
  billingMode?: "invoice" | "prepaid";
  estimatedCharge?: number;
  coverSheetAdded?: boolean;
};

export type FaxScheduleResponse = {
  ok: true;
  scheduled: true;
  scheduledSend: FaxScheduledSend;
  provider: "ictfax";
  phone: string;
  pageCount: number;
  pointsReserved: number;
  billingMode?: "invoice" | "prepaid";
  estimatedCharge?: number;
};

export type FaxScheduleRequest = FaxSendRequest & { idempotencyKey: string };

export type FaxPreviewResponse = {
  blob: Blob;
  pageCount: number | null;
  estimatedPoints: number | null;
  billingMode: "invoice" | "prepaid" | null;
  estimatedCharge: number | null;
  billingPreviewToken: string | null;
};

function appendFaxCoverSheet(fd: FormData, cover?: FaxCoverSheetInput): void {
  if (!cover) return;
  fd.append("cover_enabled", "1");
  if (cover.recipientCompany) fd.append("cover_recipient_company", cover.recipientCompany);
  if (cover.recipientName) fd.append("cover_recipient_name", cover.recipientName);
  if (cover.subject) fd.append("cover_subject", cover.subject);
  if (cover.message) fd.append("cover_message", cover.message);
  if (cover.senderContact) fd.append("cover_sender_contact", cover.senderContact);
}

function appendFaxFiles(fd: FormData, files: File[]): void {
  // 単一ファイルは既存の `file` 名を維持し、複数ファイルは順序を保つ file_0.. を使う。
  if (files.length === 1) {
    const file = files[0];
    if (file) fd.append("file", file);
    return;
  }
  files.forEach((file, index) => fd.append(`file_${index}`, file));
}

function buildFaxFormData(request: FaxSendRequest): FormData {
  const fd = new FormData();
  fd.append("phone", request.phone);
  appendFaxFiles(fd, request.files);
  if (request.accountId != null) fd.append("account_id", String(request.accountId));
  if (request.provider) fd.append("provider", request.provider);
  if (request.message) fd.append("message", request.message);
  if (request.billingPreviewToken) fd.append("billing_preview_token", request.billingPreviewToken);
  appendFaxCoverSheet(fd, request.cover);
  return fd;
}

function buildFaxScheduleFormData(request: FaxSendRequest, scheduledAt: string): FormData {
  const fd = buildFaxFormData(request);
  fd.append("scheduled_at", scheduledAt);
  return fd;
}

function parseFaxPreviewResponse(response: { blob: Blob; headers: Headers }): FaxPreviewResponse {
  const pageCountHeader = response.headers.get("X-Fax-Page-Count");
  const pageCount = pageCountHeader == null ? null : Number(pageCountHeader);
  const estimatedPointsHeader = response.headers.get("X-Fax-Estimated-Points");
  const estimatedPoints = estimatedPointsHeader == null ? null : Number(estimatedPointsHeader);
  const billingModeHeader = response.headers.get("X-Fax-Billing-Mode");
  const estimatedChargeHeader = response.headers.get("X-Fax-Estimated-Charge");
  const billingPreviewToken = response.headers.get("X-Fax-Billing-Preview-Token");
  const estimatedCharge = estimatedChargeHeader == null ? null : Number(estimatedChargeHeader);
  return {
    blob: response.blob,
    pageCount: pageCount != null && Number.isFinite(pageCount) && pageCount > 0 ? pageCount : null,
    estimatedPoints: estimatedPoints != null && Number.isFinite(estimatedPoints) && estimatedPoints >= 0
      ? estimatedPoints
      : null,
    billingMode: billingModeHeader === "invoice" || billingModeHeader === "prepaid" ? billingModeHeader : null,
    estimatedCharge: estimatedCharge != null && Number.isFinite(estimatedCharge) && estimatedCharge >= 0
      ? estimatedCharge
      : null,
    billingPreviewToken: billingPreviewToken?.trim() || null,
  };
}

export const faxRecipientsApi = {
  list: (params: {
    q?: string;
    favorite?: boolean;
    recent?: boolean;
    limit?: number;
    page?: number;
  } = {}) => {
    const query = new URLSearchParams();
    if (params.q) query.set("q", params.q);
    if (params.favorite !== undefined) query.set("favorite", String(params.favorite));
    if (params.recent !== undefined) query.set("recent", String(params.recent));
    if (params.limit != null) query.set("limit", String(params.limit));
    if (params.page != null) {
      const effectiveLimit = params.limit ?? 50;
      query.set("offset", String(Math.max(0, params.page - 1) * effectiveLimit));
    }
    const suffix = query.toString();
    return api.get<FaxRecipientListResponse>(`/fax-recipients${suffix ? `?${suffix}` : ""}`);
  },
  search: (q: string, limit = 8) => faxRecipientsApi.list({ q, limit }),
  favorites: (limit = 8) => api.get<FaxRecipientListResponse>(`/fax-recipients/favorites?limit=${limit}`),
  recent: (limit = 8) => api.get<FaxRecipientListResponse>(`/fax-recipients/recent?limit=${limit}`),
  create: (input: FaxRecipientInput) => api.post<{ recipient: FaxRecipient; contact?: FaxRecipient }>("/fax-recipients", sanitizeFaxRecipientInput(input)),
  update: (id: string, input: Partial<FaxRecipientInput>) =>
    api.patch<{ recipient: FaxRecipient; contact?: FaxRecipient }>(`/fax-recipients/${encodeURIComponent(id)}`, sanitizeFaxRecipientInput(input)),
  patch: (id: string, input: Partial<FaxRecipientInput>) =>
    faxRecipientsApi.update(id, input),
  archive: (id: string) => api.delete<{ status?: string; ok?: boolean; recipient?: FaxRecipient }>(`/fax-recipients/${encodeURIComponent(id)}`),
  remove: (id: string) => faxRecipientsApi.archive(id),
};

function sanitizeFaxRecipientInput(input: Partial<FaxRecipientInput>): Record<string, unknown> {
  return {
    ...(input.companyName !== undefined ? { companyName: input.companyName } : {}),
    ...(input.contactName !== undefined ? { contactName: input.contactName } : {}),
    ...(input.faxNumber !== undefined ? { faxNumber: input.faxNumber } : {}),
    ...(input.defaultAccountId !== undefined ? { defaultAccountId: input.defaultAccountId } : {}),
    ...(input.favorite !== undefined ? { favorite: input.favorite } : {}),
    ...(input.notes !== undefined ? { notes: input.notes } : {}),
  };
}

const faxSendDraftsClient = {
  list: (params: { status?: string; limit?: number; offset?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.limit != null) query.set("limit", String(params.limit));
    if (params.offset != null) query.set("offset", String(params.offset));
    const suffix = query.toString();
    return api.get<{ data?: FaxSendDraft[]; drafts?: FaxSendDraft[]; total?: number; limit?: number; offset?: number }>(`/fax-send-drafts${suffix ? `?${suffix}` : ""}`);
  },
  get: async (id: string): Promise<{ draft: FaxSendDraft }> => {
    try {
      return await api.get<{ draft: FaxSendDraft }>(`/fax-send-drafts/${encodeURIComponent(id)}`);
    } catch (error) {
      // persistence MVPは一覧APIのみのため、404時は一覧から同一IDを復元する。
      if (!(error instanceof Error) || !("status" in error) || (error as { status?: number }).status !== 404) throw error;
      const response = await faxSendDraftsClient.list({ limit: 200 });
      const draft = (response.drafts ?? response.data ?? []).find((row) => row.id === id);
      if (!draft) throw error;
      return { draft };
    }
  },
  create: (input: FaxSendDraftInput) => api.post<{ draft: FaxSendDraft }>("/fax-send-drafts", sanitizeFaxSendDraftInput(input)),
  update: (id: string, input: Partial<FaxSendDraftInput>) =>
    api.patch<{ draft: FaxSendDraft }>(`/fax-send-drafts/${encodeURIComponent(id)}`, sanitizeFaxSendDraftInput(input)),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/fax-send-drafts/${encodeURIComponent(id)}`),
};

function sanitizeFaxSendDraftInput(input: Partial<FaxSendDraftInput>): Record<string, unknown> {
  const coverMetadata = input.coverMetadata ?? input.cover;
  return {
    ...(input.fileName !== undefined ? { fileName: input.fileName } : {}),
    ...(input.phone !== undefined ? { phone: input.phone } : {}),
    ...(input.accountId !== undefined ? { accountId: input.accountId } : {}),
    ...(input.provider !== undefined ? { provider: input.provider } : {}),
    ...(coverMetadata !== undefined ? { coverMetadata } : {}),
  };
}

function createFaxIdempotencyKey(): string {
  const browserCrypto = globalThis.crypto;
  if (typeof browserCrypto?.randomUUID === "function") return browserCrypto.randomUUID();
  if (typeof browserCrypto?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    browserCrypto.getRandomValues(bytes);
    return `fax-${Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")}`;
  }
  return `fax-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function sendFax(request: FaxSendRequest): Promise<FaxSendResponse>;
function sendFax(
  phone: string,
  file: File,
  accountId?: number,
  provider?: FaxProvider,
  message?: string,
  cover?: FaxCoverSheetInput,
  idempotencyKey?: string,
  files?: File[],
): Promise<FaxSendResponse>;
function sendFax(
  requestOrPhone: FaxSendRequest | string,
  file?: File,
  accountId?: number,
  provider?: FaxProvider,
  message?: string,
  cover?: FaxCoverSheetInput,
  idempotencyKey?: string,
  files?: File[],
): Promise<FaxSendResponse> {
  const request: FaxSendRequest = typeof requestOrPhone === "string"
    ? {
        phone: requestOrPhone,
        files: files?.length ? files : file ? [file] : [],
        accountId,
        provider,
        message,
        cover,
        idempotencyKey,
      }
    : requestOrPhone;
  if (request.files.length === 0) {
    return Promise.reject(new Error("送信原稿を1件以上選択してください"));
  }
  // `/send` は常にIdempotency-Keyを要求する。旧シグネチャから呼ばれた場合も
  // クライアント側で補完し、送信の二重受付を防ぐ。
  const headers = { "Idempotency-Key": request.idempotencyKey || createFaxIdempotencyKey() };
  return api.upload<FaxSendResponse>("/send", buildFaxFormData(request), headers);
}

export const sendApi = {
  send: sendFax,
  schedule: (request: FaxScheduleRequest, scheduledAt: string) => {
    if (request.files.length === 0) return Promise.reject(new Error("送信原稿を1件以上選択してください"));
    if (!request.idempotencyKey) {
      return Promise.reject(new Error("予約送信にはIdempotency-Keyが必要です"));
    }
    const headers = { "Idempotency-Key": request.idempotencyKey };
    return api.upload<FaxScheduleResponse>("/send/schedule", buildFaxScheduleFormData(request, scheduledAt), headers);
  },
  preview: async (request: FaxSendRequest): Promise<FaxPreviewResponse> => {
    if (request.files.length === 0) throw new Error("送信原稿を1件以上選択してください");
    const response = await api.uploadBlobResponse("/send/preview", buildFaxFormData(request));
    return parseFaxPreviewResponse(response);
  },
  // 旧送付状MVPクライアントとの互換メソッド。内部では常に同じpreview経路を使う。
  previewCoverSheet: (phone: string, file: File, cover: FaxCoverSheetInput) =>
    sendApi.preview({ phone, files: [file], provider: "ictfax", cover }),
  status: (txId: number) =>
    api.get<{ transmissionId: number; status: string; normalizedStatus?: string; statusDetail?: string | null; isStale?: boolean }>(`/send/status/${txId}`),
  queueStatus: (queueItemId: string) =>
    api.get<{
      queueItemId: string;
      transmissionId?: number | null;
      status: string;
      normalizedStatus?: string;
      statusDetail?: string | null;
      submittedAt?: string | null;
      completedAt?: string | null;
      createdAt?: string;
    }>(`/send/queue-status/${queueItemId}`),
  accounts: () => api.get<Array<{ account_id: number; phone: string; type: string }>>("/send/accounts"),
  recipients: faxRecipientsApi,
  faxRecipients: faxRecipientsApi,
  drafts: faxSendDraftsClient,
  scheduled: {
    list: (params: { status?: string; limit?: number } = {}) => {
      const query = new URLSearchParams();
      if (params.status) query.set("status", params.status);
      if (params.limit != null) query.set("limit", String(params.limit));
      const suffix = query.toString();
      return api.get<{ scheduledSends: FaxScheduledSend[] }>(`/fax-scheduled-sends${suffix ? `?${suffix}` : ""}`);
    },
    cancel: (id: string) => api.post<{ scheduledSend: FaxScheduledSend }>(
      `/fax-scheduled-sends/${encodeURIComponent(id)}/cancel`,
      {},
    ),
  },
};

export const faxSendDraftsApi = faxSendDraftsClient;

// Users
export type UserManagementRecord = {
  id: string;
  email: string;
  name: string;
  picture: string | null;
  role: string;
  lastLogin: string | null;
  createdAt: string;
  emailVerified: boolean;
  invoiceRecipient: boolean;
  invitationExpiresAt: string | null;
};

export type UserInvitationInput = {
  email: string;
  name: string;
  role: "viewer" | "admin";
};

export const usersApi = {
  list: () => api.get<UserManagementRecord[]>("/users"),
  invite: (input: UserInvitationInput) =>
    api.post<{
      ok: true;
      user?: UserManagementRecord;
      invitationExpiresAt?: string | null;
    }>("/users/invitations", input),
  resendInvitation: (id: string) =>
    api.post<{ ok: true; invitationExpiresAt?: string | null }>(
      `/users/${encodeURIComponent(id)}/invitation/resend`,
      {},
    ),
  revokeInvitation: (id: string) =>
    api.post<{ ok: true }>(
      `/users/${encodeURIComponent(id)}/invitation/revoke`,
      {},
    ),
  setInvoiceRecipient: (id: string, enabled: boolean) =>
    api.put<{ ok: true }>(
      `/users/${encodeURIComponent(id)}/invoice-recipient`,
      { enabled },
    ),
  updateRole: (id: string, role: string, stepUpNonce?: string) =>
    stepUpNonce
      ? api.putWithHeaders<{ ok: boolean }>(`/users/${id}/role`, { role }, {
        "X-Platform-Admin-Step-Up": stepUpNonce,
      })
      : api.put<{ ok: boolean }>(`/users/${id}/role`, { role }),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/users/${id}`),
};

export interface FaxBoxAccessSettings {
  enabled: boolean;
  faxNumbers: Array<{
    id: string;
    faxNumber: string;
    active: boolean;
    notes: string | null;
  }>;
  users: Array<{
    id: string;
    name: string;
    email: string;
    role: string;
    tenantFaxNumberIds: string[];
    faxNumberAssignments?: Array<{
      tenantFaxNumberId: string;
      notificationMethods: string[];
    }>;
    notificationMethodsByFaxNumberId?: Record<string, string[]>;
  }>;
}

export const faxBoxAccessApi = {
  get: () => api.get<FaxBoxAccessSettings>("/settings/fax-box-access"),
  updateUser: (
    userId: string,
    tenantFaxNumberIds: string[],
    notificationMethodsByFaxNumberId: Record<string, string[]>,
  ) =>
    api.put<{
      ok: true;
      tenantFaxNumberIds: string[];
      notificationMethodsByFaxNumberId?: Record<string, string[]>;
    }>(
      `/settings/fax-box-access/users/${encodeURIComponent(userId)}`,
      { tenantFaxNumberIds, notificationMethodsByFaxNumberId },
    ),
};

// Dictionary
export const dictionaryApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<ProductAlias>>(`/dictionary?${qs}`);
  },
  create: (data: { type: string; alias: string; canonicalCode?: string; canonicalName: string }) =>
    api.post<{ ok: boolean; entry: ProductAlias; created?: boolean }>("/dictionary", data),
  update: (id: string, data: { alias?: string; canonicalCode?: string; canonicalName?: string }) =>
    api.put<{ ok: boolean; entry: ProductAlias }>(`/dictionary/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/dictionary/${id}`),
  import: (rows: Array<{ type: string; alias: string; canonicalCode?: string; canonicalName: string }>) =>
    api.post<{ ok: boolean; created: number; skipped: number; errors: Array<{ row: number; error: string }> }>(
      "/dictionary/import",
      { rows },
    ),
  suggestions: () =>
    api.get<{ data: DictionarySuggestion[] }>("/dictionary/suggestions"),
  suggestionsCount: () =>
    api.get<{ count: number }>("/dictionary/suggestions/count"),
  decideSuggestion: (data: {
    field: string;
    originalValue: string;
    correctedValue: string;
    status: DictionarySuggestionDecisionStatus;
    reason?: DictionarySuggestionRejectReason;
  }) =>
    api.post<{ ok: boolean }>("/dictionary/suggestions/decision", data),
};

// Product Mapping
export const productMappingApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<ProductMapping>>(`/product-mapping?${qs}`);
  },
  create: (data: { sourceCode: string; replacementCode: string; replacementName: string; notes?: string }) =>
    api.post<{ ok: boolean; entry: ProductMapping }>("/product-mapping", data),
  update: (id: string, data: { sourceCode?: string; replacementCode?: string; replacementName?: string; notes?: string; active?: boolean }) =>
    api.put<{ ok: boolean; entry: ProductMapping }>(`/product-mapping/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/product-mapping/${id}`),
  import: (rows: Array<{ sourceCode: string; replacementCode: string; replacementName: string; notes?: string }>) =>
    api.post<{ ok: boolean; created: number; skipped: number; errors: Array<{ row: number; error: string }> }>(
      "/product-mapping/import",
      { rows },
    ),
  apply: (id: string) => api.post<{ ok: boolean }>(`/product-mapping/${id}/apply`, {}),
};

// Import Code Maps
export const importCodeMapApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<ImportCodeMap>>(`/import-code-maps?${qs}`);
  },
  create: (data: {
    namespace: string;
    keyType: string;
    keyValue: string;
    canonicalCode?: string;
    canonicalName: string;
    payloadJson?: string | Record<string, unknown> | null;
    sourceType?: string;
    sourceName?: string;
    externalId?: string;
    active?: boolean;
  }) => api.post<{ ok: boolean; entry: ImportCodeMap }>("/import-code-maps", data),
  update: (id: string, data: Partial<{
    namespace: string;
    keyType: string;
    keyValue: string;
    canonicalCode: string;
    canonicalName: string;
    payloadJson: string | Record<string, unknown> | null;
    sourceType: string;
    sourceName: string;
    externalId: string;
    active: boolean;
  }>) => api.put<{ ok: boolean; entry: ImportCodeMap }>(`/import-code-maps/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/import-code-maps/${id}`),
  import: (
    rows: Array<{
      namespace?: string;
      keyType?: string;
      key_type?: string;
      keyValue?: string;
      key_value?: string;
      canonicalCode?: string;
      canonical_code?: string;
      canonicalName?: string;
      canonical_name?: string;
      payloadJson?: string | Record<string, unknown> | null;
      payload_json?: string | Record<string, unknown> | null;
      sourceType?: string;
      source_type?: string;
      sourceName?: string;
      source_name?: string;
      externalId?: string;
      external_id?: string;
      active?: boolean | string | number;
    }>,
    defaults?: { sourceType?: string; sourceName?: string },
  ) =>
    api.post<{ ok: boolean; upserted: number; skipped: number; errors: Array<{ row: number; error: string }> }>(
      "/import-code-maps/import",
      { rows, ...(defaults ?? {}) },
    ),
};

// Bulk Send
export const bulkApi = {
  send: (faxFile: File, csvFile: File) => {
    const fd = new FormData();
    fd.append("file", faxFile);
    fd.append("csv", csvFile);
    return api.upload<{
      ok: boolean;
      documentId: number;
      total: number;
      queued?: number;
      succeeded: number;
      failed: number;
      resultsTruncated?: boolean;
      resultSampleLimit?: number;
      results: Array<{ phone: string; queueItemId?: string; error?: string }>;
    }>("/bulk", fd);
  },
  history: () =>
    api.get<{ data: BulkRun[] }>("/bulk/history"),
  historyDetail: (id: string) =>
    api.get<BulkRunDetail>(`/bulk/history/${encodeURIComponent(id)}`),
  cancel: (id: string) =>
    api.post<{ ok: boolean; cancelled: number; draining: number }>(`/bulk/history/${encodeURIComponent(id)}/cancel`, {}),
};

export interface BulkRun {
  id: string;
  fileName: string | null;
  total: number;
  status: "running" | "paused" | "completed" | "failed" | "cancelled";
  succeeded: number;
  failed: number;
  cancelled: number;
  pending: number;
  queued: number;
  throughput: number;
  etaMinutes: number;
  createdByEmail: string | null;
  startedAt: string;
  completedAt: string | null;
}

export interface BulkRunDetail extends BulkRun {
  errors: Array<{ phone: string; error: string }>;
}

// Billing
export const billingApi = {
  usage: (yearMonth?: string) => {
    const path = yearMonth ? `/billing/usage/${yearMonth}` : "/billing/usage";
    return api.get<UsageSummary>(path);
  },
  plan: () => api.get<BillingPlan>("/billing/plan"),
  invoices: () => api.get<{ data: Invoice[] }>("/billing/invoices"),
  invoice: (id: string) => api.get<InvoiceDetail>(`/billing/invoices/${id}`),
  generateInvoice: (tenantId: string, year: number, month: number) =>
    api.post<{ ok: boolean; invoiceId: string; invoiceNumber: string }>(
      "/billing/invoices/generate",
      { tenantId, year, month },
    ),
  issueInvoice: (tenantId: string, year: number, month: number) =>
    api.post<{
      ok: boolean;
      invoiceId: string;
      invoiceNumber: string;
      status: string;
      total: number;
      notificationSent: boolean;
      alreadyNotified: boolean;
      zeroAmount: boolean;
      notificationStatus: string;
      notificationError: string | null;
      freeeSyncStatus: string;
      freeeDealId: string | null;
      freeeSyncError: string | null;
      pdfReady: boolean;
      pdfError: string | null;
    }>("/billing/invoices/issue", { tenantId, year, month }),
  updateInvoiceStatus: (id: string, status: string) =>
    api.put<{ ok: boolean; invoice: Invoice }>(`/billing/invoices/${id}/status`, { status }),
  addLineItem: (invoiceId: string, data: { description: string; unitPrice: number; quantity: number }) =>
    api.post<{ ok: boolean; invoice: InvoiceDetail }>(`/billing/invoices/${invoiceId}/line-items`, data),
  updateLineItem: (invoiceId: string, lineItemId: string, data: { description?: string; unitPrice?: number; quantity?: number }) =>
    api.put<{ ok: boolean; invoice: InvoiceDetail }>(`/billing/invoices/${invoiceId}/line-items/${lineItemId}`, data),
  deleteLineItem: (invoiceId: string, lineItemId: string) =>
    api.delete<{ ok: boolean; invoice: InvoiceDetail }>(`/billing/invoices/${invoiceId}/line-items/${lineItemId}`),
  points: () => api.get<PointBalance>("/billing/points"),
  pointHistory: (page?: number, limit?: number) => {
    const params = new URLSearchParams();
    if (page) params.set("page", String(page));
    if (limit) params.set("limit", String(limit));
    return api.get<{ data: PointTransaction[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/billing/points/history?${params}`);
  },
  createCheckout: (amount: number) =>
    api.post<{ url: string }>("/stripe/checkout", { amount }),
  autoCharge: () => api.get<AutoChargeResponse>("/billing/auto-charge"),
  setupAutoCharge: (input: AutoChargeSetupInput) =>
    api.post<{ url: string }>("/stripe/auto-charge/setup", input),
  updateAutoCharge: (input: AutoChargeSettingsInput) =>
    api.put<{ ok: true }>("/billing/auto-charge", input),
  disableAutoCharge: () =>
    api.post<{ ok: true; pendingAttemptExists: boolean }>("/billing/auto-charge/disable", {}),
  cancelPendingAutoCharge: () =>
    api.post<{ ok: true; outcome: "none" | "canceled" | "settled" }>("/stripe/auto-charge/cancel-pending", {}),
};

// API Keys (admin+)
export const apiKeysApi = {
  list: () => api.get<{ data: ApiKey[]; policy: ApiKeyManagementPolicy }>("/api-keys"),
  create: (data: { name: string; expiresAt?: string; scopes: ApiKeyScope[]; allowedSourceCidrs: string[]; requireIdempotency: boolean; replaceActiveTrialKey?: boolean }) =>
    api.post<ApiKeyCreateResponse>("/api-keys", data),
  updateSourceCidrs: (id: string, allowedSourceCidrs: string[]) =>
    api.patch<{ ok: true; allowedSourceCidrs: string[] }>(`/api-keys/${id}/source-cidrs`, { allowedSourceCidrs }),
  revoke: (id: string) => api.delete<{ ok: boolean }>(`/api-keys/${id}`),
};

// Platform Billing (platform_admin only)
export const platformBillingApi = {
  getBillingPlan: (tenantId: string) =>
    api.get<BillingPlan>(`/platform/tenants/${tenantId}/billing-plan`),
  summary: () => api.get<{ data: BillingSummaryItem[] }>("/platform/billing/summary"),
  generateAll: (year: number, month: number) =>
    api.post<{
      ok: boolean;
      total: number;
      succeeded: number;
      failed: number;
      results: Array<{ tenantId: string; tenantName: string; ok: boolean; invoiceNumber?: string; error?: string }>;
    }>("/platform/billing/generate-all", { year, month }),
  issueAll: (year: number, month: number, stepUpNonce: string) =>
    api.postWithHeaders<{
      ok: boolean;
      total: number;
      succeeded: number;
      failed: number;
      results: Array<{
        tenantId: string;
        tenantName: string;
        ok: boolean;
        invoiceNumber?: string;
        notificationStatus?: string;
        freeeSyncStatus?: string;
        error?: string;
      }>;
    }>("/platform/billing/issue-all", { year, month }, {
      "X-Platform-Admin-Step-Up": stepUpNonce,
    }),
  updateBillingPlan: (tenantId: string, data: Partial<BillingPlan>) =>
    api.put<{ ok: boolean; plan: BillingPlan }>(`/platform/tenants/${tenantId}/billing-plan`, data),
  getPoints: (tenantId: string) =>
    api.get<PointBalance>(`/platform/tenants/${tenantId}/points`),
  adjustPoints: (tenantId: string, amount: number, description: string) =>
    api.post<{ ok: boolean; balance: number; transaction: PointTransaction }>(`/platform/tenants/${tenantId}/points/adjust`, { amount, description }),
  getPointHistory: (tenantId: string, page?: number) => {
    const params = new URLSearchParams();
    if (page) params.set("page", String(page));
    return api.get<{ data: PointTransaction[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/platform/tenants/${tenantId}/points/history?${params}`);
  },
};

// Campaign (platform_admin only)
export const campaignApi = {
  segments: () =>
    api.get<{ data: Array<{ id: string; name: string; target: string; totalNumbers: number; recommendedTemplate: string; batchCount: number }> }>("/campaign/segments"),
  createSegment: (data: { name: string; target: string; recommendedTemplate: string; csv: File }) => {
    const fd = new FormData();
    fd.append("name", data.name);
    fd.append("target", data.target);
    fd.append("recommendedTemplate", data.recommendedTemplate);
    fd.append("csv", data.csv);
    return api.upload<{
      ok: boolean;
      segment: { id: string; name: string; target: string; totalNumbers: number; recommendedTemplate: string; batchCount: number };
      duplicatesRemoved: number;
    }>("/campaign/segments", fd);
  },
  deleteSegment: (id: string) =>
    api.delete<{ ok: boolean }>(`/campaign/segments/${encodeURIComponent(id)}`),
  templates: () =>
    api.get<{ data: Array<{ id: string; name: string; appeal: string; target: string; headline: string; previewUrl: string }> }>("/campaign/templates"),
  previewUrl: (id: string) => `/api/campaign/templates/${id}/preview`,
  send: (data: { segmentId: string; templateId: string; dryRun?: boolean; intervalMs?: number; confirmDuplicates?: boolean; skipDuplicates?: boolean }) =>
    api.post<{ ok: boolean; campaignId?: string; total?: number; totalBatches?: number; dryRun?: boolean; estimatedCost?: number; skippedDuplicates?: number; requiresConfirmation?: boolean; duplicates?: string[]; duplicateCount?: number; newCount?: number; totalCount?: number }>("/campaign/send", data),
  stop: (id: string) =>
    api.post<{ ok: boolean }>(`/campaign/${id}/stop`, {}),
  pause: (id: string) =>
    api.post<{ ok: boolean }>(`/campaign/${id}/pause`, {}),
  resume: (id: string) =>
    api.post<{ ok: boolean }>(`/campaign/${id}/resume`, {}),
  status: (id: string) =>
    api.get<{
      id: string; segmentId: string; templateId: string;
      total: number; queued: number; succeeded: number; failed: number; cancelled: number;
      retrying: number; throughput: number; etaMinutes: number;
      currentBatch: number; totalBatches: number;
      phase: "queuing" | "waiting" | "paused" | "done";
      status: "running" | "paused" | "completed" | "failed" | "cancelled";
      startedAt: string; completedAt?: string; dryRun: boolean;
      errors: Array<{ phone: string; error: string }>; totalErrors: number;
    }>(`/campaign/status/${id}`),
  list: () =>
    api.get<{ data: Array<{ id: string; segmentId: string; templateId: string; total: number; queued: number; succeeded: number; failed: number; cancelled: number; phase: string; status: string; startedAt: string; completedAt?: string; dryRun: boolean }> }>("/campaign/list"),
  transmissions: (id: string, opts?: { status?: string; sort?: string; order?: string; limit?: number; offset?: number }) => {
    const p = new URLSearchParams();
    if (opts?.status) p.set("status", opts.status);
    if (opts?.sort) p.set("sort", opts.sort);
    if (opts?.order) p.set("order", opts.order);
    if (opts?.limit) p.set("limit", String(opts.limit));
    if (opts?.offset) p.set("offset", String(opts.offset));
    const qs = p.toString() ? `?${p.toString()}` : "";
    return api.get<{
      data: Array<{ id: string; campaignId: string; phone: string; ictfaxTxId: number | null; status: string; error: string | null; createdAt: string; submittedAt: string | null; completedAt: string | null }>;
      summary: { total: number; queued: number; completed: number; failed: number };
      pagination?: { limit: number; offset: number; total: number };
    }>(`/campaign/${id}/transmissions${qs}`);
  },
};

// Chat
export const chatApi = {
  exportQuality: (params?: { since?: string; until?: string; limit?: number }) => {
    const search = new URLSearchParams();
    if (params?.since) search.set("since", params.since);
    if (params?.until) search.set("until", params.until);
    if (params?.limit) search.set("limit", String(params.limit));
    return api.getBlob(`/chat/conversations/quality-export?${search}`);
  },
  listConversations: (brand: "sphere" | "spfax", status?: string) => {
    const params = new URLSearchParams();
    params.set("brand", brand);
    if (status) params.set("status", status);
    return api.get<{ data: Array<{
      id: string; brand: string; visitorName: string | null; visitorEmail: string | null;
      supportState: string | null; claimedBy: string | null; claimedByEmail: string | null;
      claimedAt: string | null; firstResponseAt: string | null; visitorFetchedHumanAt: string | null; slaDueAt: string | null;
      slaAlertedAt: string | null; handoffCount: number; loopDetectedAt: string | null;
      visitorCompany: string | null; visitorPhone: string | null;
      appUserId: string | null; appUserEmail: string | null; appUserName: string | null;
      appTenantId: string | null; appTenantName: string | null;
      lastSeenAt: string | null; lastLeftAt: string | null; visitorTypingAt: string | null;
      humanSeenAt: string | null; humanLeftAt: string | null; humanTypingAt: string | null;
      status: string; source: string; messageCount: number;
      maCid: string | null; firstReferrer: string | null; landingPage: string | null; currentUrl: string | null;
      utmSource: string | null; utmMedium: string | null; utmCampaign: string | null; utmTerm: string | null; utmContent: string | null;
      userAgent: string | null;
      lastMessage: { content: string; role: string; createdAt: string } | null;
      createdAt: string; updatedAt: string;
    }> }>(`/chat/conversations?${params}`);
  },
  getMessages: (brand: "sphere" | "spfax", conversationId: string) =>
    api.get<{
      messages: Array<{ id: string; role: string; content: string; createdAt: string }>;
      status: string;
      presence?: {
        visitorSeenAt: string | null;
        visitorLeftAt: string | null;
        visitorTypingAt: string | null;
        humanSeenAt: string | null;
        humanLeftAt: string | null;
        humanTypingAt: string | null;
      };
    }>(
      `/chat/admin/conversations/${conversationId}/messages?brand=${brand}`,
    ),
  presence: (brand: "sphere" | "spfax", conversationId: string, event: "seen" | "left" | "typing") =>
    api.post<{ ok: boolean; event: string }>(`/chat/conversations/${conversationId}/admin-presence?brand=${brand}`, { event }),
  reply: (brand: "sphere" | "spfax", conversationId: string, content: string) =>
    api.post<{ ok: boolean; message: { id: string; role: string; content: string; createdAt: string } }>(
      `/chat/conversations/${conversationId}/reply?brand=${brand}`,
      { content },
    ),
  claim: (brand: "sphere" | "spfax", conversationId: string) =>
    api.post<{ ok: boolean; claimedBy: string; claimedByEmail: string }>(
      `/chat/conversations/${conversationId}/claim?brand=${brand}`,
      {},
    ),
  release: (brand: "sphere" | "spfax", conversationId: string) =>
    api.post<{ ok: boolean; forced: boolean }>(`/chat/conversations/${conversationId}/release?brand=${brand}`, {}),
  close: (brand: "sphere" | "spfax", conversationId: string) =>
    api.post<{ ok: boolean }>(`/chat/conversations/${conversationId}/close?brand=${brand}`, {}),
  listQa: (source?: string) => {
    const params = new URLSearchParams();
    if (source) params.set("source", source);
    return api.get<{ data: Array<{
      id: string; question: string; answer: string; category: string | null;
      source: string; usageCount: number; showOnLp: boolean; lpPages: string; sortOrder: number;
      sourceRef: string | null; verifiedAt: string | null; reviewAfter: string | null;
      createdAt: string; updatedAt: string;
    }> }>(`/chat/qa?${params}`);
  },
  listQaSuggestions: (status?: "pending" | "approved" | "rejected" | "all") => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    return api.get<{ data: Array<{
      id: string;
      conversationId: string | null;
      question: string;
      answer: string;
      category: string | null;
      source: string;
      status: "pending" | "approved" | "rejected";
      origin: string | null;
      confidence: number | null;
      grounding: string | null;
      gateResult: string | null;
      gateReason: string | null;
      riskCategory: string | null;
      citations: string | null;
      numericClaims: string | null;
      reviewedBy: string | null;
      reviewedAt: string | null;
      createdAt: string;
      updatedAt: string;
    }> }>(`/chat/qa/suggestions?${params}`);
  },
  approveQaSuggestion: (id: string, data?: {
    question?: string;
    answer?: string;
    category?: string;
    showOnLp?: boolean;
    lpPages?: string;
    sortOrder?: number;
    confirmHighRisk?: boolean;
  }) => api.post<{ ok: boolean; entry: unknown; suggestion: unknown }>(`/chat/qa/suggestions/${id}/approve`, data ?? {}),
  rejectQaSuggestion: (id: string) =>
    api.post<{ ok: boolean; suggestion: unknown }>(`/chat/qa/suggestions/${id}/reject`, {}),
  createQa: (data: { question: string; answer: string; category?: string; sourceRef?: string; showOnLp?: boolean; lpPages?: string; sortOrder?: number }) =>
    api.post<{ ok: boolean; entry: unknown }>("/chat/qa", data),
  updateQa: (id: string, data: { question?: string; answer?: string; category?: string; sourceRef?: string; reverify?: boolean; showOnLp?: boolean; lpPages?: string; sortOrder?: number }) =>
    api.put<{ ok: boolean; entry: unknown }>(`/chat/qa/${id}`, data),
  deleteQa: (id: string) =>
    api.delete<{ ok: boolean }>(`/chat/qa/${id}`),
};

// Self-Company Identification
export const selfCompanyApi = {
  get: () => api.get<{ names: string[]; faxNumbers: string[] }>("/settings/self-company"),
  update: (data: { names: string[]; faxNumbers: string[] }) =>
    api.put<{ ok: boolean; names: string[]; faxNumbers: string[] }>("/settings/self-company", data),
};

// Received-FAX → multi-recipient email forwarding
export const faxReceiveForwardApi = {
  get: () => api.get<{ emails: string[]; mode: "attachment" | "notification_only"; lockedBySecurityProfile: boolean }>("/settings/fax-receive-forward"),
  update: (emails: string[], mode: "attachment" | "notification_only") =>
    api.put<{ ok: boolean; emails: string[]; mode: "attachment" | "notification_only" }>("/settings/fax-receive-forward", { emails, mode }),
};

export interface SecurityProfileSettings {
  securityProfile: "standard" | "confidential" | "medical";
  mfaRequired: boolean;
  faxReceiveForwardMode: "attachment" | "notification_only";
  ocrDataClass: "standard" | "confidential" | "medical";
  ocrAllowGlobalProcessing: boolean;
  ocrRetentionDays: number | null;
  outboundFaxRetentionDays: number | null;
  outboundFaxRetentionOperational: boolean;
  userCount: number;
  missingMfaUsers: number;
}

export const securityProfileApi = {
  get: () => api.get<SecurityProfileSettings>("/settings/security-profile"),
  update: (profile: SecurityProfileSettings["securityProfile"], retentionDays?: number) =>
    api.put<{ ok: true } & Omit<SecurityProfileSettings, "userCount" | "missingMfaUsers">>("/settings/security-profile", {
      profile,
      ...(retentionDays === undefined ? {} : { retentionDays }),
    }),
};

// Email Intake
export interface SmtpIntakeCredential {
  id: string;
  username: string;
  active: boolean;
  allowedRcptAddress: string;
  allowedSourceCidrs: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface EmailIntakeSettingsResponse {
  emailToken: string | null;
  intakeAddress: string | null;
  emailTokenConfigured: boolean;
  domain: string;
  smtpIntake?: {
    host: string;
    port: number;
    credentials: SmtpIntakeCredential[];
  };
}

export interface SmtpIntakeCredentialCreateResponse {
  id: string;
  username: string;
  password: string;
  allowedRcptAddress: string;
  host: string;
  port: number;
}

export const emailIntakeApi = {
  get: () => api.get<EmailIntakeSettingsResponse>("/settings/email-intake"),
  generate: (reset = false, stepUpNonce?: string) => stepUpNonce
    ? api.postWithHeaders<{ emailToken: string; intakeAddress: string; domain: string; created: boolean }>(
      "/settings/email-intake/generate",
      { reset },
      { "X-Platform-Admin-Step-Up": stepUpNonce },
    )
    : api.post<{ emailToken: string; intakeAddress: string; domain: string; created: boolean }>(
      "/settings/email-intake/generate",
      { reset },
    ),
  createSmtpCredential: (stepUpNonce?: string) => stepUpNonce
    ? api.postWithHeaders<SmtpIntakeCredentialCreateResponse>(
      "/settings/email-intake/smtp-credentials",
      {},
      { "X-Platform-Admin-Step-Up": stepUpNonce },
    )
    : api.post<SmtpIntakeCredentialCreateResponse>("/settings/email-intake/smtp-credentials", {}),
  deleteSmtpCredential: (id: string, stepUpNonce?: string) => stepUpNonce
    ? api.deleteWithHeaders<{ ok: boolean }>(
      `/settings/email-intake/smtp-credentials/${id}`,
      { "X-Platform-Admin-Step-Up": stepUpNonce },
    )
    : api.delete<{ ok: boolean }>(`/settings/email-intake/smtp-credentials/${id}`),
};

// Platform (platform_admin only)
export type SupportAccessSession = {
  id: string;
  tenantId: string;
  tenantName: string;
  actorEmail: string;
  reason: string;
  ticketReference: string | null;
  accessLevel: "read_only" | "ocr_reprocess" | "dm_reply_classify";
  startedAt: string;
  expiresAt: string;
};

export type PlatformAdminAccountInventoryStatus =
  | "active"
  | "expired"
  | "missing_expiry"
  | "missing_user"
  | "role_not_allowlisted";

export type PlatformAdminAccountInventory = {
  generatedAt: string;
  summary: {
    allowedCount: number;
    currentRoleCount: number;
    activeCount: number;
    findingCount: number;
  };
  accounts: Array<{
    email: string;
    userId: string | null;
    ownerName: string | null;
    tenantId: string | null;
    lastLogin: string | null;
    createdAt: string | null;
    expiresAt: string | null;
    canGrantPlatformAdmin: boolean;
    status: PlatformAdminAccountInventoryStatus;
  }>;
};

export type SupportDmReplyPageResponse = {
  page_number: number;
  search_code?: string;
  selected_actions: string[];
  selection_confidence: "high" | "medium" | "low";
  qr_url?: string;
};

export type SupportDmReplyCandidateResponse = {
  tenant: { id: string; name: string };
  ocrResultId: string;
  ocrVersion: string;
  reviewReason:
    | "no_search_code"
    | "tracking_link_not_found"
    | "multiple_search_codes"
    | "no_selected_action"
    | "conflicting_actions"
    | "respondent_needs_review"
    | "selected_action";
  analysis: {
    searchCodes: string[];
    actions: string[];
    pageResponses?: SupportDmReplyPageResponse[];
  };
  candidates: Array<{
    trackingLinkId: string;
    searchCode: string;
    campaignId: string;
    campaignName: string;
    advertiserName: string | null;
    recipientId: string;
    recipientCompany: string | null;
    recipientContactName: string | null;
    recipientFaxNumberMasked: string;
  }>;
};

export type SupportDmReplyConfirmPayload = {
  tenantId: string;
  outcome: "positive" | "unsubscribe" | "normal";
  campaignId: string;
  recipientId: string;
  searchCode: string;
  ocrVersion: string;
  note: string;
  selectedActions: string[];
  acknowledgeConflict: boolean;
  acknowledgeTarget: true;
};

export type SupportDmReplyConfirmResponse = {
  ok: true;
  operationId: string;
  outcome: "positive" | "unsubscribe" | "normal";
  faxType: string;
  idempotent: boolean;
};

export type PortingCaseStatus = "new" | "contacting" | "awaiting_customer" | "ready_for_application" | "closed";
export type PortingCaseAssigneeRole = "admin" | "sales" | "internal_support" | "platform_admin";
export type PortingCaseWaitingFor = "customer" | "internal" | "carrier" | "none";
export type PortingCaseAssignmentFilter = "all" | "unassigned" | "assigned";
export type PortingCaseDueFilter = "all" | "overdue" | "none";

export type PortingCase = {
  id: string;
  company: string;
  name: string;
  email: string;
  phone: string | null;
  source: string | null;
  notifyStatus: string;
  portingCaseStatus: PortingCaseStatus;
  portingCaseUpdatedAt: string | null;
  portingCaseUpdatedBy: string | null;
  assigneeUserId: string | null;
  assigneeRole: PortingCaseAssigneeRole | null;
  waitingFor: PortingCaseWaitingFor | null;
  dueAt: string | null;
  nextAction: string | null;
  portingNumber: string | null;
  portingNumberCount: string | null;
  currentProvider: string | null;
  installationPrefecture: string | null;
  desiredTiming: string | null;
  createdAt: string;
  portingPrecheck: {
    id: string;
    classification: string;
    lineType: string;
    dedicatedType: string;
    connectionType: string;
  } | null;
  portingAssessment: {
    id: string;
    diagnosticId: string;
    normalizedNumber: string;
    serviceAddress: string;
    lineType: string;
    dedicated: string;
    numberRole: string;
    numberUsage: string;
    lineSpecialUse: string;
    coverageStatus: string;
    impactStatus: string;
    overallStatus: string;
    reasonCodes: unknown;
    missingFields: unknown;
    evaluatedAt: string | null;
    createdAt: string | null;
  } | null;
};

export const platformApi = {
  getPlatformAdminAccountInventory: () =>
    api.get<PlatformAdminAccountInventory>("/platform/admin/platform-admin-accounts"),
  listTenants: (params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<{ data: PlatformTenant[] }>(`/platform/tenants?${qs}`);
  },
  getRuntimeControls: () =>
    api.get<{ runtimeControls: RuntimeControls; uploadOcr: UploadOcrStatus; faxQueue: FaxSchedulerHealth | null }>("/platform/runtime-controls"),
  updateRuntimeControls: (data: {
    signupEnabled?: boolean;
    ocrGlobalPause?: boolean;
    ocrFreightAnalysisGlobalPause?: boolean;
    dmReplyPageSplitEnabled?: boolean;
  }) =>
    api.put<{ ok: boolean; runtimeControls: RuntimeControls }>("/platform/runtime-controls", data),
  recoverFaxQueue: (data: { olderThanSeconds?: number; dryRun?: boolean }) =>
    api.post<{
      ok: boolean;
      dryRun: boolean;
      olderThanSeconds: number;
      recoveredDispatchingNoTx: number;
      recoveredDispatchingWithTx: number;
      failedSubmittedWithoutTx: number;
      skippedSubmittedWithTx: number;
    }>("/platform/admin/fax-queue/recover", data),
  getTenant: (id: string) =>
    api.get<PlatformTenantDetail>(`/platform/tenants/${id}`),
  getCurrentSupportSession: () =>
    api.get<{ session: SupportAccessSession | null; code?: string }>("/platform/support-sessions/current"),
  startSupportSession: (data: {
    tenantId: string;
    reason: string;
    ticketReference?: string;
    accessLevel: "read_only" | "ocr_reprocess" | "dm_reply_classify";
    durationMinutes: number;
  }) =>
    api.post<{ session: SupportAccessSession }>("/platform/support-sessions", data),
  endSupportSession: () =>
    api.delete<{ ok: true }>("/platform/support-sessions/current"),
  listSupportSuppressions: (
    tenantId: string,
    params?: { active?: boolean; limit?: number; offset?: number },
  ) => {
    const query = new URLSearchParams({ tenantId });
    if (params?.active !== undefined) query.set("active", String(params.active));
    if (params?.limit != null) query.set("limit", String(params.limit));
    if (params?.offset != null) query.set("offset", String(params.offset));
    return api.get<DmSuppressionsResponse>(
      `/platform/support-sessions/current/suppressions?${query.toString()}`,
    );
  },
  getSupportDmReplyCandidate: (tenantId: string, ocrResultId: string) =>
    api.get<SupportDmReplyCandidateResponse>(
      `/platform/support-sessions/current/dm-replies/${encodeURIComponent(ocrResultId)}/candidate?tenantId=${encodeURIComponent(tenantId)}`,
    ),
  confirmSupportDmReply: (
    ocrResultId: string,
    data: SupportDmReplyConfirmPayload,
  ) =>
    api.post<SupportDmReplyConfirmResponse>(
      `/platform/support-sessions/current/dm-replies/${encodeURIComponent(ocrResultId)}/confirm`,
      data,
    ),
  createTenant: (data: {
    name: string;
    domain: string;
    didNumber: string;
    ictfaxUsername: string;
    ictfaxPassword: string;
  }) =>
    api.post<{
      tenant: PlatformTenant;
      provisioning: {
        ictfaxUserId: number;
        didId: number;
        stepsCompleted: string[];
      };
    }>("/platform/tenants", data),
  updateTenant: (
    id: string,
    data: {
      name?: string;
      faxHeaderName?: string | null;
      costPerSecond?: number;
      plan?: string;
    },
  ) => api.put<{ ok: boolean; tenant: PlatformTenant }>(`/platform/tenants/${id}`, data),
  updateTenantActiveState: (
    tenantId: string,
    data: { active: boolean; reason: string },
    stepUpNonce: string,
  ) => api.putWithHeaders<{ ok: boolean; tenant: Pick<PlatformTenant, "id" | "active"> }>(
    `/platform/tenants/${tenantId}/active-state`,
    data,
    { "X-Platform-Admin-Step-Up": stepUpNonce },
  ),
  updateTenantSendPolicy: (
    tenantId: string,
    data: {
      lifecycleStatus?: PlatformTenant["lifecycleStatus"];
      verificationStatus?: PlatformTenant["verificationStatus"];
      sendMode?: PlatformTenant["sendMode"];
      trialExpiresAt?: string | null;
      archivedAt?: string | null;
      reason: string;
    },
    stepUpNonce: string,
  ) => api.putWithHeaders<{ ok: boolean; tenant: Pick<
    PlatformTenant,
    "id" | "lifecycleStatus" | "verificationStatus" | "sendMode" | "trialExpiresAt" | "archivedAt"
  > }>(
    `/platform/tenants/${tenantId}/send-policy`,
    data,
    { "X-Platform-Admin-Step-Up": stepUpNonce },
  ),
  updateEntraOidcPolicy: (
    tenantId: string,
    data: { enabled: boolean; entraTenantId?: string; recoveryReason?: string },
    stepUpNonce: string,
  ) => api.putWithHeaders<{
    ok: boolean;
    entraTenantId: string | null;
    entraOidcOnlyEnabled: boolean;
  }>(`/platform/tenants/${tenantId}/entra-oidc-policy`, data, {
    "X-Platform-Admin-Step-Up": stepUpNonce,
  }),
  enableTenantSendDemo: (id: string, data: { reason: string }, stepUpNonce: string) =>
    api.postWithHeaders<{
      ok: boolean;
      tenant: PlatformTenant;
      pointBalance: number;
      topUp: number;
      demo: { days: number; ocrTrialPoints: number };
    }>(`/platform/tenants/${id}/send-demo`, data, {
      "X-Platform-Admin-Step-Up": stepUpNonce,
    }),
  addAllowedFaxRecipient: (
    tenantId: string,
    data: { phone: string; label?: string; verificationReason?: string },
  ) =>
    api.post<{ ok: boolean; entry: AllowedFaxRecipient }>(
      `/platform/tenants/${tenantId}/allowed-fax-recipients`,
      data,
    ),
  removeAllowedFaxRecipient: (tenantId: string, recipientId: string) =>
    api.delete<{ ok: boolean }>(
      `/platform/tenants/${tenantId}/allowed-fax-recipients/${recipientId}`,
    ),
  addDomain: (tenantId: string, domain: string) =>
    api.post<{ ok: boolean; mapping: { id: string; domain: string } }>(
      `/platform/tenants/${tenantId}/domains`,
      { domain },
    ),
  removeDomain: (tenantId: string, domainId: string) =>
    api.delete<{ ok: boolean }>(
      `/platform/tenants/${tenantId}/domains/${domainId}`,
    ),
  createUser: (
    tenantId: string,
    data: { email: string; name: string; password: string; role: string },
    stepUpNonce: string,
  ) =>
    api.postWithHeaders<{ ok: boolean; user: { id: string; email: string; name: string; role: string } }>(
      `/platform/tenants/${tenantId}/users`,
      data,
      { "X-Platform-Admin-Step-Up": stepUpNonce },
    ),
  resetPassword: (tenantId: string, userId: string, password: string, stepUpNonce: string) =>
    api.putWithHeaders<{ ok: boolean }>(
      `/platform/tenants/${tenantId}/users/${userId}/password`,
      { password },
      { "X-Platform-Admin-Step-Up": stepUpNonce },
    ),
  listApiKeys: (tenantId: string) =>
    api.get<{ data: ApiKey[] }>(`/platform/tenants/${tenantId}/api-keys`),
  createApiKey: (
    tenantId: string,
    data: { name: string; expiresAt?: string },
    stepUpNonce: string,
  ) => api.postWithHeaders<ApiKeyCreateResponse>(
    `/platform/tenants/${tenantId}/api-keys`,
    data,
    { "X-Platform-Admin-Step-Up": stepUpNonce },
  ),
  revokeApiKey: (tenantId: string, keyId: string, stepUpNonce: string) =>
    api.deleteWithHeaders<{ ok: boolean }>(
      `/platform/tenants/${tenantId}/api-keys/${keyId}`,
      { "X-Platform-Admin-Step-Up": stepUpNonce },
    ),
  getOcrCosts: (tenantId: string) =>
    api.get<{
      data: Array<{
        id: string;
        transmissionId: number | null;
        source: string;
        senderNumber: string | null;
        senderEmail: string | null;
        originalFileName: string | null;
        faxType: string | null;
        processedAt: string;
        inputTokens: number | null;
        outputTokens: number | null;
        costUsd: number;
      }>;
      model: string;
      pricing: { input: number; output: number };
      totalCostUsd: number;
    }>(`/platform/tenants/${tenantId}/ocr-costs`),
  notifySubscribers: () =>
    api.get<{ data: Array<{ id: string; email: string; name: string | null; company: string | null; phone: string | null; desiredNumber: string | null; areaCode: string | null; status: string | null; note: string | null; source: string | null; interest: string | null; maCid: string | null; createdAt: string }> }>("/platform/notify-subscribers"),
  portingCases: (params?: {
    status?: "active" | "all" | PortingCaseStatus;
    take?: number;
    assignment?: PortingCaseAssignmentFilter;
    due?: PortingCaseDueFilter;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.take) qs.set("take", String(params.take));
    if (params?.assignment) qs.set("assignment", params.assignment);
    if (params?.due) qs.set("due", params.due);
    const suffix = qs.toString() ? `?${qs}` : "";
    return api.get<{
      data: PortingCase[];
      summary: {
        statusCounts: Partial<Record<PortingCaseStatus, number>>;
        activeCount: number;
        assignmentCounts: { all: number; assigned: number; unassigned: number };
        dueCounts: { all: number; overdue: number; none: number };
      };
    }>(`/platform/porting-cases${suffix}`);
  },
  updatePortingCase: (
    id: string,
    data: {
      expectedStatus: PortingCaseStatus;
      status: PortingCaseStatus;
      reason: string;
      expectedUpdatedAt?: string | null;
    },
  ) => api.patch<{ ok: true; changed: boolean; status: PortingCaseStatus; updatedAt: string | null }>(
    `/platform/porting-cases/${encodeURIComponent(id)}`,
    data,
  ),
  updatePortingCaseWorkflow: (
    id: string,
    data: {
      expectedUpdatedAt: string | null;
      assigneeUserId: string | null;
      assigneeRole: PortingCaseAssigneeRole | null;
      waitingFor: PortingCaseWaitingFor;
      dueAt: string | null;
      nextAction: string | null;
    },
  ) => api.patch<{
    ok: true;
    changed: boolean;
    workflow: {
      assigneeUserId: string | null;
      assigneeRole: PortingCaseAssigneeRole | null;
      waitingFor: PortingCaseWaitingFor | null;
      dueAt: string | null;
      nextAction: string | null;
      updatedAt: string | null;
    };
  }>(`/platform/porting-cases/${encodeURIComponent(id)}/workflow`, data),
  leads: (params?: { interest?: string; take?: number }) => {
    const qs = new URLSearchParams();
    if (params?.interest) qs.set("interest", params.interest);
    if (params?.take) qs.set("take", String(params.take));
    const suffix = qs.toString() ? `?${qs}` : "";
    return api.get<{
      data: Array<{
        id: string;
        emailDomain: string | null;
        companyName: string | null;
        contact: { name: string | null; company: string | null; phone: string | null; email: string | null };
        portingContext: {
          precheckId: string | null;
          classification: string | null;
          lineType: string | null;
          dedicatedType: string | null;
          connectionType: string | null;
          number: string | null;
          numberCount: string | null;
          provider: string | null;
          prefecture: string | null;
          desiredTiming: string | null;
        } | null;
        maCid: string | null;
        firstReferrer: string | null;
        landingPage: string | null;
        landingUrl: string | null;
        utmSource: string | null;
        utmMedium: string | null;
        utmCampaign: string | null;
        firstSeenAt: string;
        lastSeenAt: string;
        events: Array<{
          id: string;
          eventType: string;
          interest: string | null;
          sourceTable: string | null;
          sourceId: string | null;
          page: string | null;
          source: string | null;
          errorCode: string | null;
          createdAt: string;
        }>;
      }>;
      summary: {
        eventCounts: Record<string, number>;
        rawEventCounts: Record<string, number>;
        uniqueSourceEntityCounts: Record<string, number>;
        uniqueLeadCounts: Record<string, number>;
        interestCounts: Record<string, number>;
      };
    }>(`/platform/leads${suffix}`);
  },
  aiAttribution: (days = 30) =>
    api.get<AiAttributionStats>(`/platform/attribution/ai-sources?days=${encodeURIComponent(String(days))}`),
  cvFunnelReport: (params: {
    from: string;
    to: string;
    attributionModel: "first" | "last_non_direct";
  }) => {
    const qs = new URLSearchParams(params);
    return api.get<CvFunnelReport>(`/platform/attribution/funnel-report?${qs.toString()}`);
  },
  listDemoTenants: () =>
    api.get<{ data: DemoTenantSummary[]; testFaxNumbers: string[] }>("/platform/demo-tenants"),
  createDemoTenant: (data: {
    companyName: string;
    customerName: string;
    email: string;
    domain?: string;
    trialDays: number;
  }) =>
    api.post<{
      ok: boolean;
      tenant: {
        id: string;
        name: string;
        trialExpiresAt: string | null;
        lifecycleStatus: string;
        verificationStatus: string;
        sendMode: string;
      };
      user: { id: string; email: string; name: string; role: string };
      temporaryPassword: string;
      loginUrl: string;
      testFaxNumbers: string[];
      ocrTrial: { points: number; perPageFee: number; estimatedPages: number };
      faxProvisioning: {
        status: "pending" | "provisioned" | "already_provisioned" | "failed";
        stepsCompleted: string[];
        failedStep?: string;
      };
    }>("/platform/demo-tenants", data),
  provisionDemoTenantFax: (tenantId: string) =>
    api.post<{
      ok: boolean;
      faxProvisioning: {
        status: "provisioned" | "already_provisioned";
        stepsCompleted: string[];
      };
      reloginRequired: boolean;
    }>(`/platform/demo-tenants/${encodeURIComponent(tenantId)}/provision-fax`, {}),
  listTrialApplications: (params?: { status?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.limit) qs.set("limit", String(params.limit));
    return api.get<{
      data: TrialApplicationItem[];
      summary: Record<string, number>;
      canOperateNumbers: boolean;
      trialPlatformEnabled: boolean;
    }>(`/platform/trial-applications?${qs.toString()}`);
  },
  getTrialApplicationSummary: () => api.get<{
    summary: Record<string, number>;
    trialPlatformEnabled: boolean;
  }>("/platform/trial-applications/summary"),
  createTrialApplication: (data: {
    tenantId: string;
    requestedUseCase: string;
    requestedFeatures: Array<"inbound" | "ocr" | "outbound">;
    needsExternalSend: boolean;
    plannedRecipient?: string;
  }) => api.post<{ ok: true; grant: { id: string; status: "pending_review" } }>(
    "/platform/trial-applications",
    data,
  ),
  approveTrialApplication: (
    id: string,
    data: {
      businessIdentityChecked: true;
      useCaseAllowed: true;
      temporaryNumberTermsAccepted: true;
      reviewReference: string;
      reviewNotes?: string;
    },
  ) => api.put<{ ok: true; status: "approved" }>(
    `/platform/trial-applications/${id}/approve`,
    data,
  ),
  rejectTrialApplication: (
    id: string,
    data: { reviewReference: string; reason: string },
  ) => api.put<{ ok: true; status: "rejected" }>(
    `/platform/trial-applications/${id}/reject`,
    data,
  ),
  reserveTrialApplicationNumber: (
    id: string,
    data: { inventoryId: string; reservationReference: string },
  ) => api.put<{ ok: true; status: "reserved"; faxNumber: string }>(
    `/platform/trial-applications/${id}/reserve-number`,
    data,
  ),
  activateTrialApplication: (
    id: string,
    data: {
      tenantFaxNumberId: string;
      physicalRouteReady: true;
      ictfaxTenantMatched: true;
      noPendingInbound: true;
      customerNoticeReady: true;
      provisioningReference: string;
    },
  ) => api.put<{
    ok: true;
    status: "active";
    startsAt: string;
    expiresAt: string;
    sendMode: "test_only" | "allowlist";
  }>(`/platform/trial-applications/${id}/activate`, data),
  extendTrialApplication: (
    id: string,
    data: {
      extensionDays: number;
      customerRequested: true;
      usageAndConversionReviewed: true;
      approvalReference: string;
      reason: string;
    },
  ) => api.put<{
    ok: true;
    status: "extended";
    previousExpiresAt: string;
    expiresAt: string;
    extensionCount: number;
  }>(`/platform/trial-applications/${id}/extend`, data),
  listIntakeRecords: (params?: {
    status?: string;
    tenantId?: string;
    receiver?: string;
    token?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.tenantId) qs.set("tenantId", params.tenantId);
    if (params?.receiver) qs.set("receiver", params.receiver);
    if (params?.token) qs.set("token", params.token);
    if (params?.limit) qs.set("limit", String(params.limit));
    return api.get<{
      data: PlatformIntakeRecord[];
      summary: Record<string, number>;
    }>(`/platform/admin/intake-records?${qs.toString()}`);
  },
  listTrialFaxNumbers: (params?: {
    status?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.limit) qs.set("limit", String(params.limit));
    return api.get<{
      data: TrialFaxNumberInventoryItem[];
      summary: Record<string, number>;
    }>(`/platform/admin/trial-fax-numbers?${qs.toString()}`);
  },
  confirmTrialFaxNumberSuspension: (
    inventoryId: string,
    data: {
      physicalRouteStopped: true;
      confirmationReference: string;
      note?: string;
    },
  ) =>
    api.put<{
      ok: true;
      idempotent: boolean;
      inventory: {
        id: string;
        status: "suspended";
        suspendedAt: string | null;
      };
    }>(
      `/platform/admin/trial-fax-numbers/${inventoryId}/confirm-suspension`,
      data,
    ),
  startTrialFaxNumberReturnCheck: (
    inventoryId: string,
    data: {
      noPendingInbound: true;
      confirmationReference: string;
      note?: string;
    },
  ) =>
    api.put<{
      ok: true;
      idempotent: boolean;
      inventory: {
        id: string;
        status: "return_check";
        returnCheckStartedAt: string | null;
        returnCheckUntil: string | null;
      };
    }>(
      `/platform/admin/trial-fax-numbers/${inventoryId}/start-return-check`,
      data,
    ),
  retryTrialFaxNumberReturnCheck: (
    inventoryId: string,
    data: {
      physicalRouteStillStopped: true;
      noPendingInbound: true;
      confirmationReference: string;
      investigationSummary: string;
    },
  ) =>
    api.put<{
      ok: true;
      inventory: {
        id: string;
        status: "return_check";
        returnCheckStartedAt: string;
        returnCheckUntil: string;
      };
    }>(
      `/platform/admin/trial-fax-numbers/${inventoryId}/retry-return-check`,
      data,
    ),
  retireTrialFaxNumber: (
    inventoryId: string,
    data: {
      providerNumberRetired: true;
      confirmationReference: string;
      investigationSummary: string;
    },
  ) =>
    api.put<{
      ok: true;
      idempotent: boolean;
      inventory: {
        id: string;
        status: "retired";
        retiredAt: string | null;
      };
    }>(
      `/platform/admin/trial-fax-numbers/${inventoryId}/retire`,
      data,
    ),
  listTrialNotifications: (params?: { status?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.limit) qs.set("limit", String(params.limit));
    return api.get<{
      data: TrialNotificationDeliveryItem[];
      summary: Record<string, number>;
    }>(`/platform/admin/trial-notifications?${qs.toString()}`);
  },
  resolveTrialNotificationSent: (
    deliveryId: string,
    data: {
      sesDeliveryConfirmed: true;
      confirmationReference: string;
      note?: string;
    },
  ) => api.put<{
    ok: true;
    idempotent: boolean;
    delivery: { id: string; status: "sent"; resolvedAt: string | null };
  }>(`/platform/admin/trial-notifications/${deliveryId}/resolve-sent`, data),
  retryTrialNotification: (
    deliveryId: string,
    data: {
      sesNonDeliveryConfirmed: true;
      confirmationReference: string;
      note?: string;
    },
  ) => api.put<{
    ok: true;
    delivery: { id: string; status: "pending"; resolvedAt: string };
  }>(`/platform/admin/trial-notifications/${deliveryId}/retry`, data),
  listTrialDataRetentions: (params?: { status?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.limit) qs.set("limit", String(params.limit));
    return api.get<{
      data: TrialDataRetentionItem[];
      summary: Record<string, number>;
    }>(`/platform/admin/trial-data-retentions?${qs.toString()}`);
  },
  confirmTrialDataRetentionInventory: (
    retentionId: string,
    data: {
      ictfaxReviewed: true;
      s3Reviewed: true;
      localStorageReviewed: true;
      confirmationReference: string;
      note?: string;
    },
  ) => api.put<{
    ok: true;
    idempotent: boolean;
    deletionPerformed: false;
    retention: {
      id: string;
      status: "ready_for_deletion";
      reviewedAt: string | null;
    };
  }>(`/platform/admin/trial-data-retentions/${retentionId}/confirm-inventory`, data),
  listTrialConversions: (params?: { status?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.limit) qs.set("limit", String(params.limit));
    return api.get<{
      data: TrialConversionItem[];
      summary: Record<string, number>;
      canOperate: boolean;
    }>(`/platform/admin/trial-conversions?${qs.toString()}`);
  },
  listTrialCases: (params?: { phase?: TrialCasePhase | "all"; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.phase) qs.set("phase", params.phase);
    if (params?.limit) qs.set("limit", String(params.limit));
    return api.get<{
      data: TrialCaseItem[];
      summary: Record<TrialCasePhase, number>;
      readOnly: true;
    }>(`/platform/admin/trial-cases?${qs.toString()}`);
  },
  previewTrialConversionPreparation: (
    trialGrantId: string,
    data: {
      targetPlan: "ocr_only" | "fax_send" | "fax_full";
      targetSendMode: "disabled" | "allowlist" | "normal";
      targetDmSendEnabled: boolean;
      billingMode: "invoice" | "prepaid";
      monthlyBaseFee: number;
      perFaxSendFee: number;
      perOcrPageFee: number;
      productionDid?: string | null;
      productionSenderAccountId?: number | null;
      productionTenantFaxNumberId?: string | null;
      productionApiKeyId?: string | null;
      contractReference: string;
      businessVerificationReference: string;
      billingReference: string;
      productionResourceReference: string;
      apiSecurityReference: string;
    },
  ) => api.post<{ ok: true } & TrialConversionPreparationPreview>(
    `/platform/admin/trial-conversions/${trialGrantId}/preview-prepare`,
    data,
  ),
  prepareTrialConversion: (
    trialGrantId: string,
    data: {
      contractConfirmed: true;
      businessVerificationConfirmed: true;
      billingConfirmed: true;
      productionResourcesConfirmed: true;
      apiSecurityConfirmed: true;
      temporaryNumberNonContinuationConfirmed: true;
      normalSendPolicyReviewed: true;
      previewToken: string;
      targetPlan: "ocr_only" | "fax_send" | "fax_full";
      targetSendMode: "disabled" | "allowlist" | "normal";
      targetDmSendEnabled: boolean;
      billingMode: "invoice" | "prepaid";
      monthlyBaseFee: number;
      perFaxSendFee: number;
      perOcrPageFee: number;
      productionDid?: string | null;
      productionSenderAccountId?: number | null;
      productionTenantFaxNumberId?: string | null;
      productionApiKeyId?: string | null;
      contractReference: string;
      businessVerificationReference: string;
      billingReference: string;
      productionResourceReference: string;
      apiSecurityReference: string;
    },
  ) => api.post<{
    ok: true;
    conversion: {
      id: string;
      tenantId: string;
      status: "preparing";
      suspensionRequested: boolean;
    };
  }>(`/platform/admin/trial-conversions/${trialGrantId}/prepare`, data),
  checkTrialConversionReadiness: (conversionId: string) =>
    api.post<{
      ok: true;
      status: "preparing" | "ready";
      blockers: string[];
    }>(`/platform/admin/trial-conversions/${conversionId}/check-readiness`, {}),
  previewTrialConversionPreflight: (conversionId: string) =>
    api.get<{ ok: true } & TrialConversionPreflight>(
      `/platform/admin/trial-conversions/${conversionId}/preflight`,
    ),
  finalizeTrialConversion: (
    conversionId: string,
    data: {
      finalStateReviewed: true;
      noPendingProcessingConfirmed: true;
      customerNoticeReady: true;
      conversionReference: string;
    },
  ) => api.put<{
    ok: true;
    atomic: true;
    status: "converted";
    tenantId: string;
    trialGrantId: string;
    convertedAt: string;
  }>(`/platform/admin/trial-conversions/${conversionId}/finalize`, data),
};

// Changelog (platform_admin only)
export const changelogApi = {
  list: () => api.get<{ data: Array<{ id: string; title: string; description: string; page: string; publishedAt: string; createdAt: string; updatedAt: string }> }>("/changelog"),
  feed: (audience: string = "sidebar") =>
    api.get<{ data: Array<{ id: string; title: string; description: string; publishedAt: string }> }>(
      `/changelog/feed?audience=${encodeURIComponent(audience)}`,
    ),
  create: (data: { title: string; description: string; page: string; publishedAt?: string }) =>
    api.post<{ data: { id: string; title: string; description: string; page: string; publishedAt: string } }>("/changelog", data),
  update: (id: string, data: { title?: string; description?: string; page?: string; publishedAt?: string }) =>
    api.put<{ data: { id: string; title: string; description: string; page: string; publishedAt: string } }>(`/changelog/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/changelog/${id}`),
};

export type AnnouncementSeverity = "info" | "important" | "critical";
export type AnnouncementStatus = "draft" | "pending" | "published" | "archived";

export interface AnnouncementItem {
  id: string;
  source: "system" | "tenant";
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  pinned: boolean;
  publishedAt: string;
  expiresAt: string | null;
  contentVersion: number;
  dismissed: boolean;
}

export interface AnnouncementAdminItem {
  id: string;
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  pinned: boolean;
  status: AnnouncementStatus;
  publishedAt: string | null;
  expiresAt: string | null;
  contentVersion: number;
  gateResult: string | null;
  gateReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export type AnnouncementCreateInput = {
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  pinned?: boolean;
  expiresAt?: string | null;
  status?: AnnouncementStatus;
};

export type AnnouncementUpdateInput = {
  title?: string;
  body?: string;
  severity?: AnnouncementSeverity;
  pinned?: boolean;
  expiresAt?: string | null;
};

export const announcementsApi = {
  listVisible: () => api.get<{ data: AnnouncementItem[] }>("/announcements"),
  dismiss: (id: string) => api.post<{ ok: boolean }>(`/announcements/${id}/dismiss`, {}),
  dismissTenant: (id: string) => api.post<{ ok: boolean }>(`/announcements/tenant/${id}/dismiss`, {}),
  adminList: () => api.get<{ data: AnnouncementAdminItem[] }>("/announcements/admin"),
  create: (data: AnnouncementCreateInput) =>
    api.post<{ data: AnnouncementAdminItem; queued?: number; queueError?: string }>("/announcements/admin", data),
  update: (id: string, data: AnnouncementUpdateInput) =>
    api.put<{ data: AnnouncementAdminItem }>(`/announcements/admin/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/announcements/admin/${id}`),
  publish: (id: string) =>
    api.post<{ data: AnnouncementAdminItem; queued?: number; queueError?: string }>(`/announcements/admin/${id}/publish`, {}),
};

// Onboarding Wizard
export interface CsvColumnDef {
  ocrField: string;
  headerLabel: string;
  defaultValue?: string;
  format?: string;
}

export interface CsvTemplateRow {
  id: string;
  tenantId: string;
  name: string;
  presetKey: string | null;
  encoding: string;
  columns: CsvColumnDef[];
  headerRow: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export const csvTemplateApi = {
  list: () => api.get<{ templates: CsvTemplateRow[] }>("/csv-templates"),
  presets: () => api.get<{ presets: Array<{ key: string; name: string; description: string }> }>("/csv-templates/presets"),
  create: (data: {
    name: string;
    presetKey?: string;
    encoding?: string;
    columns?: CsvColumnDef[];
    headerRow?: boolean;
    isDefault?: boolean;
  }) => api.post<CsvTemplateRow>("/csv-templates", data),
  update: (id: string, data: Partial<{
    name: string;
    encoding: string;
    columns: CsvColumnDef[];
    headerRow: boolean;
    isDefault: boolean;
  }>) => api.put<CsvTemplateRow>(`/csv-templates/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/csv-templates/${id}`),
  exportUrl: (templateId: string, params?: Record<string, string>) => {
    const p = { ...params };
    const qs = new URLSearchParams({ format: "csv", status: "approved", templateId, ...p }).toString();
    return `/api/ocr/export?${qs}`;
  },
};

// Custom Columns
export type CustomColumnDef = {
  id: string;
  key: string;
  label: string;
  source: "ocr" | "manual";
  dataType: "string" | "number" | "date" | "datetime" | "boolean";
  valueFormat: string;
  description: string;
  sortOrder: number;
};

export const columnsApi = {
  list: () => api.get<{ columns: CustomColumnDef[] }>("/settings/columns"),
  create: (data: {
    key: string;
    label: string;
    source: "ocr" | "manual";
    description?: string;
    dataType?: "string" | "number" | "date" | "datetime" | "boolean";
    valueFormat?: string;
  }) =>
    api.post<CustomColumnDef>("/settings/columns", data),
  update: (id: string, data: Partial<CustomColumnDef>) =>
    api.put<CustomColumnDef>(`/settings/columns/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/settings/columns/${id}`),
  reorder: (ids: string[]) => api.put<{ ok: boolean }>("/settings/columns/reorder", { ids }),
  restore: (id: string) => api.post<{ ok: boolean }>(`/settings/columns/${id}/restore`, {}),
};

export const onboardingApi = {
  status: () => api.get<OnboardingStatus>("/onboarding/status"),
  complete: (data: {
    industry: string;
    faxTypes: string[];
    primaryFaxType?: string;
    brandNames?: string[];
    selfFaxNumbers?: string[];
    itemIdentification?: string;
    handwritingLevel?: string;
    useConnector?: "yes" | "later" | "no";
    otherDetails?: Record<string, string>;
    marketingNotes?: string;
    monthlyVolume: string;
    currentService: string;
    desiredFields: string[];
    targetSystem: string;
    senderMatchMethod?: string;
    faxNumberOnPdf?: boolean;
    deliveryDateGranularity?: "order" | "item" | "both";
    contactFieldMode?: "order_only" | "shipping_only" | "both";
  }) => api.post<OnboardingCompleteResponse>("/onboarding/complete", data),
  skip: () => api.post<{ ok: boolean }>("/onboarding/skip", {}),
  resetAll: () => api.post<{ ok: boolean; ocrReverted: number; localStorageKeys: string[] }>("/onboarding/reset-all", {}),
  // 0AB-J 番ポ事前登録 (waitlist)。認定取得時に通知メールを受け取りたいユーザーが opt-in。
  getNumberPortingInterest: () =>
    api.get<{
      numberPortingInterest: boolean;
      numberPortingInterestAt: string | null;
      numberPortingNotifiedAt: string | null;
    }>("/onboarding/number-porting-interest"),
  setNumberPortingInterest: (interest: boolean) =>
    api.post<{
      ok: boolean;
      numberPortingInterest: boolean;
      numberPortingInterestAt: string | null;
    }>("/onboarding/number-porting-interest", { interest }),
};

// FAX Activation
export type FaxActivationPlan = {
  id: string;
  name: string;
  monthlyFee: number;
  perFaxSendFee: number;
  setupFee: number;
  features: string[];
};

export type FaxActivationStatus = {
  plan: string;
  didNumber: string | null;
  provisionedAt: string | null;
  activation: {
    id: string;
    plan: string;
    status: string;
    numberType: string | null;
    assignedDid: string | null;
    createdAt: string;
    autoProvisionJob: {
      id: string;
      status: string;
      attempt: number;
      maxAttempts: number;
      retryAfter: string | null;
      completedAt: string | null;
      lastError: string | null;
      lastErrorCode: string | null;
    } | null;
    openingApplication: {
      approvalStatus: string;
      registryDocumentStatus: string;
      postalVerifications: Array<{
        status: string;
        dispatchRequestStatus: string;
        dispatchedAt: string | null;
        expiresAt: string;
        attemptCount: number;
        maxAttempts: number;
      }>;
    } | null;
  } | null;
};

export type FaxOpeningApplicant = {
  corporateNumber: string;
  postalCode: string;
  registeredAddress: string;
  contactName: string;
  contactRole: string;
  contactPhone: string;
  usePurpose: string;
  monthlySendVolume: number;
  faxDmPurpose: boolean;
};

export type FaxOpeningAdminRecord = {
  id: string;
  tenantId: string;
  plan: string;
  status: string;
  numberType: string | null;
  portingNumber: string | null;
  portingCarrier: string | null;
  createdAt: string;
  tenant: { name: string; didNumber: string | null };
  autoProvisionJob: {
    id: string;
    status: string;
    attempt: number;
    maxAttempts: number;
    retryAfter: string | null;
    claimedAt: string | null;
    completedAt: string | null;
    lastError: string | null;
    lastErrorCode: string | null;
  } | null;
  fullProvisioning: {
    id: string;
    status: "prepared" | "provisioning" | "manual_review" | "failed" | "active" | "cancelled";
    numberInventoryId: string;
    targetSendMode: "allowlist" | "normal";
    dmSendEnabled: boolean;
    billingMode: "prepaid" | "invoice";
    monthlyBaseFee: number;
    perFaxSendFee: string;
    perOcrPageFee: string;
    setupFee: number;
    setupFeeHandling: "prepaid_charge" | "invoice_charge" | "externally_paid" | "waived";
    setupFeeReference: string | null;
    refundStatus: "not_required" | "pending" | "applied" | "failed" | "application_settlement_required";
    refundIntentId: string | null;
    provisioningAttempt: number;
    externalStartedAt: string | null;
    ictfaxUsername: string;
    ictfaxUserId: number | null;
    ictfaxDidAccountId: number | null;
    reconciliationOutcome: "provisioned_confirmed" | "cleanup_confirmed" | null;
    reconciliationReference: string | null;
    reconciledBy: string | null;
    reconciledAt: string | null;
    lastError: string | null;
    lastErrorCode: string | null;
    completedAt: string | null;
    cancelledAt: string | null;
    updatedAt: string;
  } | null;
  openingApplication: (FaxOpeningApplicant & {
    id: string;
    serviceType: string;
    companyCheckStatus: string;
    corporateLookupStatus: string;
    corporateLookupCheckedAt: string | null;
    corporateOfficialName: string | null;
    corporateOfficialPostalCode: string | null;
    corporateOfficialAddress: string | null;
    corporateLookupError: string | null;
    riskReviewStatus: string;
    registryDocumentStatus: string;
    approvalStatus: string;
    reviewNotes: string | null;
    approvedAt: string | null;
    postalVerifications: Array<{
      id: string;
      status: string;
      dispatchRequestStatus: string;
      dispatchRequestError: string | null;
      dispatchedAt: string | null;
      trackingNumber: string | null;
      expiresAt: string;
      verifiedAt: string | null;
    }>;
  }) | null;
};

export type FaxActivationOperationalAlert = {
  id: string;
  tenantId: string;
  planId: string;
  conditionCode: string;
  severity: "p1" | "p3";
  status: "open" | "acknowledged" | "resolved";
  conditionSince: string;
  firstDetectedAt: string;
  lastDetectedAt: string;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  resolvedAt: string | null;
  details: Record<string, unknown> | null;
};

export type FaxActivationNotificationDelivery = {
  id: string;
  tenantId: string;
  planId: string;
  status: "pending" | "sending" | "accepted" | "failed" | "uncertain";
  attempts: number;
  nextAttemptAt: string;
  claimedAt: string | null;
  acceptedAt: string | null;
  providerMessageId: string | null;
  didNumberSnapshot: string;
  lastError: string | null;
};

export type FaxFullActivationConfig = {
  inventoryId: string;
  targetSendMode: "allowlist" | "normal";
  dmSendEnabled: boolean;
  billingMode: "prepaid" | "invoice";
  monthlyBaseFee: number;
  perFaxSendFee: number;
  perOcrPageFee: number;
  setupFee: number;
  setupFeeHandling: "prepaid_charge" | "invoice_charge" | "externally_paid" | "waived";
  setupFeeReference?: string | null;
  contractReference: string;
  billingReference: string;
  riskControlReference: string;
  resourceReference: string;
};

export const faxActivationApi = {
  plans: () => api.get<{ plans: FaxActivationPlan[] }>("/fax-activation/plans"),
  status: () => api.get<FaxActivationStatus>("/fax-activation/status"),
  apply: (data: FaxOpeningApplicant & { plan: string; numberType?: string; portingNumber?: string; portingCarrier?: string }) =>
    api.post<{ activation: { id: string; status: string }; message: string }>("/fax-activation/apply", data),
  upgrade: (data: FaxOpeningApplicant & { numberType: string; portingNumber?: string; portingCarrier?: string }) =>
    api.post<{ message: string }>("/fax-activation/upgrade", data),
  verifyPostalCode: (code: string) =>
    api.post<{ ok: boolean; verified: boolean; message: string }>("/fax-activation/verify-postal-code", { code }),
  downgrade: () => api.post<{ message: string }>("/fax-activation/downgrade", {}),
  cancel: () => api.post<{ message: string }>("/fax-activation/cancel", {}),
};

export const faxOpeningAdminApi = {
  list: (tenantId: string) =>
    api.get<{ data: FaxOpeningAdminRecord[] }>(
      `/fax-activation/admin/list?${new URLSearchParams({ tenantId })}`,
    ),
  startExistingOpening: (tenantId: string, data: FaxOpeningApplicant) =>
    api.post<{
      activation: { id: string; status: string };
      notifications: {
        customerEmail: "sent" | "failed";
        postalDispatch: "sent" | "failed";
        adminEmail: "sent" | "failed";
      };
      message: string;
    }>(`/fax-activation/admin/tenants/${tenantId}/start-existing-opening`, data),
  generatePostalPdf: (activationId: string) =>
    api.postBlob(`/fax-activation/admin/${activationId}/generate-postal-pdf`, {}),
  generatePostalAddressLabel: (activationId: string) =>
    api.getBlob(`/fax-activation/admin/${activationId}/postal-address-label`),
  markDispatched: (activationId: string, trackingNumber: string) =>
    api.put<{ ok: true }>(`/fax-activation/admin/${activationId}/mark-dispatched`, { trackingNumber }),
  recheckCorporate: (activationId: string) =>
    api.post<{
      ok: true;
      status: string;
      checkedAt: string | null;
      officialName: string | null;
      officialPostalCode: string | null;
      officialAddress: string | null;
      error: string | null;
    }>(`/fax-activation/admin/${activationId}/recheck-corporate`, {}),
  approve: (
    activationId: string,
    data: {
      companyCheckStatus: "matched" | "manual_review" | "rejected";
      riskReviewStatus: "approved" | "manual_review" | "rejected";
      registryDocumentStatus: "not_required" | "received" | "verified" | "rejected";
      reviewNotes?: string;
    },
  ) => api.put<{ ok: true; approvedAt: string; autoProvisionQueued: boolean }>(
    `/fax-activation/admin/${activationId}/approve-opening`,
    data,
  ),
  retryAutoProvision: (activationId: string) =>
    api.post<{ ok: true }>(`/fax-activation/admin/${activationId}/retry-auto-provision`, {}),
  available050: () =>
    api.get<{
      data: Array<{ id: string; faxNumber: string; provider: string; availableAt: string | null }>;
    }>("/fax-activation/admin/full-provisioning/available-050"),
  preflightFullProvisioning: (activationId: string, data: FaxFullActivationConfig) =>
    api.post<{
      ready: boolean;
      blockers: string[];
      tenantId: string | null;
      faxNumber: string | null;
      currentBalance: number | null;
      heldBalance: number | null;
      availableBalance: number | null;
    }>(`/fax-activation/admin/${activationId}/full-provisioning/preflight`, data),
  prepareFullProvisioning: (activationId: string, data: FaxFullActivationConfig) =>
    api.post<{
      ok: true;
      plan: { id: string; status: string; activationId: string; numberInventoryId: string };
      faxNumber: string;
      currentBalance: number;
      heldBalance: number;
      availableBalance: number;
      setupFeeSettlement: "point_hold" | "externally_paid" | "waived" | "not_required";
      setupFeeHold: { id: string; status: string; amount: number; expiresAt: string } | null;
    }>(`/fax-activation/admin/${activationId}/full-provisioning/prepare`, data),
  executeFullProvisioning: (activationId: string) =>
    api.post<{
      ok: true;
      idempotent: boolean;
      status: string;
      didNumber?: string;
      ictfaxUserId?: number;
    }>(`/fax-activation/admin/${activationId}/full-provisioning/execute`, {}),
  updateFullProvisioningSendProfile: (
    activationId: string,
    data: {
      targetSendMode: "allowlist" | "normal";
      dmSendEnabled: boolean;
      riskControlReference: string;
      normalSendPolicyReviewed: boolean;
    },
  ) => api.put<{
    ok: true;
    status: "active";
    idempotent: boolean;
    sendMode: "allowlist" | "normal";
    faxSendEnabled: true;
    dmSendEnabled: boolean;
  }>(`/fax-activation/admin/${activationId}/full-provisioning/send-profile`, data),
  reconcileFullProvisioning: (
    activationId: string,
    data:
      | { outcome: "provisioned_confirmed"; planId: string; reference: string; ictfaxUserId: number; ictfaxDidAccountId: number }
      | { outcome: "cleanup_confirmed"; planId: string; reference: string; reason: string; cleanupConfirmed: true },
  ) => api.post<{ ok: boolean; status: string; didNumber?: string; refundStatus?: string; setupFeeSettlement?: "hold_released" | "legacy_refund" | "externally_paid" | "waived" | "not_required"; retryable?: boolean }>(
    `/fax-activation/admin/${activationId}/full-provisioning/reconcile`,
    data,
  ),
  cancelFullProvisioning: (activationId: string, reason: string) =>
    api.post<{ ok: boolean; status: string; refundStatus: string; setupFeeSettlement: "hold_released" | "legacy_refund" | "externally_paid" | "waived" | "not_required"; retryable?: boolean }>(
      `/fax-activation/admin/${activationId}/full-provisioning/cancel`,
      { reason },
    ),
  retryFullProvisioningRefund: (activationId: string) =>
    api.post<{ ok: boolean; status: string; refundStatus: string; retryable?: boolean }>(
      `/fax-activation/admin/${activationId}/full-provisioning/retry-refund`,
      {},
    ),
  listFullProvisioningAlerts: (tenantId: string) =>
    api.get<{ alerts: FaxActivationOperationalAlert[]; hasMore: boolean; nextCursor: string | null }>(
      `/fax-activation/admin/full-provisioning/operational-alerts?status=open&tenantId=${encodeURIComponent(tenantId)}`,
    ),
  acknowledgeFullProvisioningAlert: (alertId: string, note: string) =>
    api.post<{ ok: true; status: "acknowledged"; idempotent: boolean }>(
      `/fax-activation/admin/full-provisioning/operational-alerts/${encodeURIComponent(alertId)}/ack`,
      { note },
    ),
  listFullProvisioningNotifications: (tenantId: string) =>
    api.get<{ deliveries: FaxActivationNotificationDelivery[] }>(
      `/fax-activation/admin/full-provisioning/notifications?tenantId=${encodeURIComponent(tenantId)}`,
    ),
};
